/*!
// <copyright file="Grid.js" company="Microsoft">
//     Copyright (c) Microsoft Corporation.  All rights reserved.
// </copyright>
*/

/// <reference path="../html-binding-controls.ts" />
/// <reference path="../html-binding-handlers.ts" />
/// <reference path="ListDataProvider.js" />
/// <reference path="ListRenderer.js" />
/// <reference path="ColumnHeader/ColumnHeader.ts" />
(function () {
    'use strict';

    Globalize.addCultureInfo('en', {
        messages: {
            List_DefaultCheckBoxText: 'Select Rows ',
            List_DefaultHeaderCheckBoxText: 'Mark all rows',
            List_NoResultsMessage: "We didn't find anything to show here.",
            List_LoadingMoreData: "Loading more data...",
            List_IncSortNumber: "Sort smallest to largest",
            List_DecSortNumber: "Sort largest to smallest",
            List_IncSortTime: "Sort oldest to newest",
            List_DecSortTime: "Sort newest to oldest",
            List_IncSortString: "Sort A to Z",
            List_DecSortString: "Sort Z to A",
            List_IncSortEnum: "Sort first to last",
            List_DecSortEnum: "Sort last to first",
            List_ColumnNotFilterable: "Column is not filterable.",
            Grid_Next: "Next",
            Grid_Previous: "Previous",
            Grid_NextRowShortcut: "Move to the next row.",
            Grid_NextRowNewLineShortcut: "Create a new row at the bottom of the grid.",
            Grid_NextRowToSelectedSetShortcut: "Add the next row to the selected set",
            Grid_NextRowButNotSelectShortcut: "Move to the next row without selecting",
            Grid_PreviousRowShortcut: "Move to the previous row.",
            Grid_PreviousRowToSelectedSetShortcut: "Add the previous row to the selected set",
            Grid_PreviousRowButNotSelectShortcut: "Move to the previous row without selecting",
            Grid_PreviousPageShortcut: "Go to the previous page of data",
            Grid_NextPageShortcut: "Go to the next page of data",
            Grid_CurrentRowSelectionShortcut: "Select the current row",
            Grid_CurrentRowClearShortcut: "Clear the current row",
            Grid_SelectAllRowsShortcut: "Select all rows",
            Grid_ClearAllRowsShortcut: "Clear all rows",
            Grid_OpenGridFilterShortCut: "Open grid filtering for the current column",
            Grid_FocusColumnHeaderShortCut: "Move to the header of the current column",
            Grid_FocusActiveRowShortcut: "Move focus to the active row",
            Grid_GrowColumn: "Make column wider",
            Grid_ShrinkColumn: "Make column narrower",
            ListHeader_RegionTitle: "Header Region",
            Framework_CheckedStatus: "Checked",
            Framework_UncheckedStatus: "Unchecked",
        }
    });

    $dyn.util.headerSkip = function (headerContext, grid) {
        return $dyn.computed(function () {
            return $dyn.value(headerContext.Skip) || !$dyn.value(grid.AllowHeaderFocus);
        });
    };

    $dyn.util.markAllSkip = function (control) {
        return $dyn.ui.controlOptions(function () { return { skip: (!$dyn.util.isEnhancedTabSequenceEnabled() || !control.AllowHeaderFocus()) }; }, control);
    };

    $dyn.ui.ListMode = {
        tabular: "Tabular",
        list: "List"
    };

    $dyn.util.SyncEngineState = {
        inactive: $dyn.debug.general ? 'inactive' : 0,
        active: $dyn.debug.general ? 'active' : 1,
    };

    $dyn.util.InfiniteScrollMode = {
        slow: $dyn.debug.general ? 'slow' : 0,
        fast: $dyn.debug.general ? 'fast' : 1,
    };

    (function() {
        if ($dyn.debug.general) {
          var styleNode = document.createElement("style");
  
          document.head.appendChild(styleNode);
          styleNode.type = "text/css";
          var styleText = document.createTextNode('.listCell[data-dyn-validating="true"] { background-color: #eaeaea; }');
          styleNode.appendChild(styleText);
        }
    })();

    // Imports
    // MS Internal use only api
    var selector = $dyn.ui.selector;
    var KeyCodes = $dyn.ui.KeyCodes;
    var InputType = $dyn.ui.InputType;

    /**
     * @class List
     * @classdesc The List control.
     * @memberof $dyn.controls
     * @public
     * @param {object} data
     * @param {HTMLElement} element
     * @param {$dyn.ui.Container} parent
     */
    $dyn.controls.List = function (data, element, parent) {
        var self = this;
        var root = $(element);

        // Virtualization support:
        // List does not creates a control instance per cell.
        // It uses an active row with a full control instance.
        // It has an inactive row with a partial control instance (for example, allowedit turned off).
        // It changes the data for inactive row and create an inert clone for each row.
        // It moves the active row to wherever the selected index is.
        // List and tabular modes share the same dom structure
        // Event handler are put on the list body instead of the list rows/cells

        self.ForEachRenderCount = $dyn.observable(0);
        self.isContainer = true;
        self._isCardList = undefined;
        self.PageSize = 25;
        self.elementInSidePanel = undefined;

        self._processServerChanges = $dyn.observable(true);
        self._fastEdit = $dyn.observable(false);

        //Flag that indicates whether all resources for controls within the grid have been loaded or not
        self._allResourcesLoaded = $dyn.observable(true);

        $dyn.ui.Control.apply(this, arguments);

        self.usingDisposable($dyn.observe(self.FastEdit, function (isFastEdit) {
            if ($dyn.ax && $dyn.ax.taskRecording) {
                self._fastEdit(($dyn.debug.gridFastEdit || isFastEdit) && !$dyn.peek($dyn.ax.taskRecording.taskGuidePlaybackState));
            } else {
                self._fastEdit($dyn.debug.gridFastEdit || isFastEdit);
            }
        }));       

        if ($dyn.ax && $dyn.ax.taskRecording) {
            self.usingDisposable($dyn.observe($dyn.ax.taskRecording.taskGuidePlaybackState, function (taskGuideOn) {
                if (taskGuideOn && self._fastEdit()) {
                    self._fastEdit(false);
                }
                if (!taskGuideOn) {
                    self._fastEdit($dyn.debug.gridFastEdit || $dyn.peek(self.FastEdit));
                }
            }));
        }

        self.LayoutMinWidth = { minSizeFunction: $dyn.layout.gridLayoutMinWidth, cssClassArgument: 'listRoot' };
        self.LayoutMinHeight = { minSizeFunction: $dyn.layout.gridLayoutMinHeight, cssClassArgument: 'listRoot' };

        if (!$dyn.isObservable(self.SelectedIndex)) {
            // The selected index must be an observable value.
            self.SelectedIndex = $dyn.observable(self.SelectedIndex);
        }

        if (!$dyn.isObservable(self.GridLines)) {
            // GridLines must be an observable value.
            self.GridLines = $dyn.observable(self.GridLines);
        }

        if (!$dyn.isObservable(self.MarkedIndexes)) {
            self.MarkedIndexes = $dyn.observable(self.MarkedIndexes || []);
        }
        if (!$dyn.isObservable(self.MarkAllRecords)) {
            self.MarkAllRecords = $dyn.observable(self.MarkAllRecords);
        }

        // Represents the child hierarchy as a single observable unit.
        self.ChildHierarchy = this._rowElementGenerator(element);

        self._listBodyElement = $('.listBody', this.element)[0];

        self._dataProvider = new $dyn.controls.List.DataProvider({
            PropertyBag: this,
            GetDataId: self.GetDataId,
            GetDataVersion: self.GetDataVersion,
        });
        self._syncManager = new $dyn.controls.List.SyncManager(self, self._dataProvider);
        self._renderer = new $dyn.controls.List.Renderer(self, {
            dataProvider: self._dataProvider,
            syncEngineState: self._syncManager._syncEngineState,
            rowSyncState: self._syncManager.rowSyncState,
            invalidRow: self._syncManager.invalidRow,
            rowHasPendingEdit: self._syncManager.rowHasPendingEdit.bind(self._syncManager),
            listBody: self._listBodyElement,
            markAllRecords: self.MarkAllRecords
        });
        self._dataProvider.rowIdByPageIndexGetter = self._renderer.getRowIdByActivePageIndex.bind(self._renderer);
        self._rowOperationsManager = new $dyn.controls.List.RowOperationsManager(self, self._dataProvider, self._renderer, self._syncManager);

        self._renderer.setActiveCell(self._rowOperationsManager.getActiveCell());
        self._editManager = new $dyn.controls.List.EditManager({
            element: this._listBodyElement,
            syncManager: this._syncManager,
            renderer: this._renderer,
            getSelectedRow: function () {
                return self._selectedRow;
            },
            allowEdit: this.AllowEdit
        });

        self.fastEditingAllowed = $dyn.computed(self._fastEditingAllowed, undefined, self);
        self._surroundingBlockingDivShown = false;
        self._syncQueueEmptySubscription = null;
        self._tabbingContainerDispose = null;
        self.usingDisposable($dyn.observe(self.fastEditingAllowed, function (blockingDivAllowed) {
            if (blockingDivAllowed && !self._syncQueueEmptySubscription) {
                self._syncQueueEmptySubscription = $dyn.observe(self._syncManager.isQueueEmpty, function (syncQueueEmpty) {
                    if (!syncQueueEmpty && !self._surroundingBlockingDivShown) {
                        self.SurroundingBlockingDiv.show(self.allowedElements);
                        self._tabbingContainerDispose = $dyn.util.tabbingContainer(self.element);
                        self._surroundingBlockingDivShown = true;
                    }
                    if (syncQueueEmpty && self._surroundingBlockingDivShown) {
                        self.SurroundingBlockingDiv.hide();
                        self.SurroundingBlockingDiv.removeClass("fastEditRailsMode");
                        self._tabbingContainerDispose();
                        self._surroundingBlockingDivShown = false;
                    }
                });
            }
            if (!blockingDivAllowed && self._syncQueueEmptySubscription) {
                if (self._surroundingBlockingDivShown) {
                    self.SurroundingBlockingDiv.hide();
                    self.SurroundingBlockingDiv.removeClass("fastEditRailsMode");
                    self._tabbingContainerDispose();
                    self._surroundingBlockingDivShown = false;
                }
                self._syncQueueEmptySubscription.dispose();
                self._syncQueueEmptySubscription = null;
            }
        }));

        self._isLoading = $dyn.computed(self._getIsLoading, null, this);

        self.ChildHierarchy.valueChanged.subscribe(function (inactiveRowElement) {
            $dyn.log.list && $dyn.log.list('List: Inactive row schema changed');
            self._resetCachedInactiveItemElement($dyn.value(inactiveRowElement)[0]);
            self._rerenderOptions($dyn.peek(self._rerenderOptions) + 1);
        });

        self._headerRowElement = this.element && this.element.querySelector(".listAsTable-HeaderRow");
        self._noRowsIndicator = this.element && this.element.querySelector(".list-No-Rows-Indicator");

        self._inactiveItem = $dyn.observable();

        self.dataSourceName = $dyn.observable('');

        var lastHeaderMetadata;
        var lastControlContexts = {};
        self.ColumnHeaderMetadata = $dyn.computed(function () {
            var rowTemplates = $dyn.value(element.dynChildren);
            var headerMetadata = [];
            var listHeaderMetadata = {};
            var controlContexts = {};

            var firstRowColumns = null;
            if (rowTemplates.length) {
                firstRowColumns = $dyn.value(rowTemplates[0].dynChildren);
            }

            if (firstRowColumns && firstRowColumns.length) {
                $.each(firstRowColumns, function (index, element) {
                    var getObservableProperty = $dyn.ui.getObservableProperty;

                    var parentRole = $dyn.role(rowTemplates[0]);
                    var role = parentRole.getChildRole(element);
                    var context = $dyn.context(element);
                    var name, headerContext, lastControlContext, lastHeaderContext, lastVersion;
                    // Elements can be added to dynChilren before they are data bound so take a dependency on databound.
                    if (!role.isDataBound() && context) {
                        $dyn.log("Role %o at index %i for control %s not databound yet", role, index, element.getAttribute("data-dyn-controlname"));
                    }
                    if (context) {
                        name = context.Name;
                        lastHeaderContext = lastHeaderMetadata && lastHeaderMetadata[name];
                        lastControlContext = lastControlContexts[name];
                        lastVersion = lastHeaderContext && lastHeaderContext.Version;

                        if (name) {
                            controlContexts[name] = context;
                        }

                        if (context === lastControlContext) {
                            headerContext = lastHeaderContext;
                        } else {
                            // Access Enabled and Width so that they are added to the computed scope and
                            // any change to them will cause a render.
                            // Note: just getting the property but not accessing the value does not add it to the scope
                            var enabledProperty = getObservableProperty(context, 'Enabled');
                            $dyn.value(enabledProperty);

                            var widthProperty = getObservableProperty(context, 'Width');
                            $dyn.value(widthProperty);

                            headerContext = {
                                FormDataSourceName: getObservableProperty(context, 'DataSourceName'),
                                IsImage: context.IsImage,
                                IsNumber: context.IsNumber,
                                Name: name,
                                Visible: getObservableProperty(context, 'Visible'),
                                Enabled: enabledProperty,
                                Alignment: getObservableProperty(context, 'Alignment'),
                                IsFiltered: getObservableProperty(context, 'IsFiltered'),
                                IsSorted: getObservableProperty(context, 'SortDirection'),
                                IsPrimarySort: getObservableProperty(context, 'IsPrimarySort'),
                                Label: self.getColumnLabel(
                                    getObservableProperty(context, 'Label'),
                                    context.Name,
                                    getObservableProperty(context, 'ShowLabel')),
                                Width: widthProperty,
                                IsFilterable: self.getFilterability(
                                    getObservableProperty(context, 'IsFilterable'),
                                    getObservableProperty(context, 'FieldName'),
                                    self.DataSource),
                                FieldName: getObservableProperty(context, 'FieldName'),
                                IsSortable: getObservableProperty(context, 'IsFilterable'),
                                ControlType: getObservableProperty(context, 'TypeName'),
                                IsLookupField: getObservableProperty(context, 'LookupButton'),
                                IsBound: true,
                                HelpText: getObservableProperty(context, 'HelpText'),
                                Skip: getObservableProperty(context, 'Skip'),
                                Version: lastVersion !== undefined ? lastVersion + 1 : 1,
                                ExecutePersonalizationCommand: getObservableProperty(context, "ExecutePersonalizationCommand"),
                                PersistPersonalization: getObservableProperty(context, "PersistPersonalization"),
                                Required: getObservableProperty(context, "Required")
                            };
                        }

                        headerContext.FilteringFields = [];

                        if (context.TypeName == "ReferenceGroup") {
                            headerContext.IsLookupField = true;

                            // Filtering is performed in the context of a Reference Group's replacement fields. Add additional context to be consumed by
                            // the filtering system.
                            var childControls = $dyn.value(context.Children);
                            childControls.forEach(function (child) {
                                var childFilterContext = {
                                    IsFilterable: self.getFilterability(
                                        getObservableProperty(child, 'IsFilterable'),
                                        getObservableProperty(child, 'FieldName'),
                                        self.DataSource),
                                    FormDataSourceName: getObservableProperty(child, 'DataSourceName'),
                                    FieldName: getObservableProperty(child, 'FieldName'),
                                    ControlType: child.TypeName,
                                    Visible: getObservableProperty(child, 'Visible'),
                                    Label: self.getColumnLabel(
                                        getObservableProperty(child, 'Label'),
                                        child.Name,
                                        getObservableProperty(child, 'ShowLabel')),
                                    Name: child.Name
                                };

                                headerContext.FilteringFields.push(childFilterContext);
                            });
                        } else if (context.TypeName == "SegmentedEntry") {
                            // Segmented entry display value is stored in a reference table linked through the field's EDT
                            // eg. Table.LedgerDimension (int64 RecId) -> DimensionAttributeValueCombination.DisplayValue (str)
                            var childFilterContext = {
                                IsFilterable: self.getFilterability(
                                    getObservableProperty(context, 'IsFilterable'),
                                    getObservableProperty(context, 'FieldName'),
                                    self.DataSource),
                                FormDataSourceName: context.ReferenceValueDataSourceName,
                                FieldName: context.ReferenceValueDataSourceFieldName,
                                ControlType: context.TypeName,
                                Visible: getObservableProperty(context, 'Visible'),
                                Label: self.getColumnLabel(
                                    getObservableProperty(context, 'Label'),
                                    context.Name,
                                    getObservableProperty(context, 'ShowLabel')),
                                Name: context.Name
                            };

                            headerContext.FilteringFields.push(childFilterContext);
                        } else {
                            var childFilterContext = {
                                IsFilterable: self.getFilterability(
                                    getObservableProperty(context, 'IsFilterable'),
                                    getObservableProperty(context, 'FieldName'),
                                    self.DataSource),
                                FormDataSourceName: getObservableProperty(context, 'DataSourceName'),
                                FieldName: getObservableProperty(context, 'FieldName'),
                                ControlType: context.TypeName,
                                Visible: getObservableProperty(context, 'Visible'),
                                Label: self.getColumnLabel(
                                    getObservableProperty(context, 'Label'),
                                    context.Name,
                                    getObservableProperty(context, 'ShowLabel')),
                                Name: context.Name
                            };

                            headerContext.FilteringFields.push(childFilterContext);
                        }

                    } else {
                        name = element.getAttribute("data-dyn-controlname");
                        lastHeaderContext = lastHeaderMetadata && lastHeaderMetadata[name];
                        lastVersion = lastHeaderContext && lastHeaderContext.Version;

                        if (lastHeaderContext && lastHeaderContext.IsBound === false) {
                            headerContext = lastHeaderContext;
                        } else {
                            headerContext = {
                                FormDataSourceName: self.DataSourceName,
                                IsImage: false,
                                IsNumber: false,
                                Name: name,
                                IsFiltered: $dyn.observable(false),
                                IsSorted: $dyn.observable(-1),
                                IsPrimarySort: $dyn.observable(false),
                                Visible: true,
                                Enabled: undefined,
                                Alignment: undefined,
                                Label: undefined,
                                Width: undefined,
                                IsFilterable: $dyn.observable(false),
                                FieldName: "",
                                ControlType: "",
                                IsLookupField: false,
                                IsBound: false,
                                Skip: undefined,
                                Version: lastVersion !== undefined ? lastVersion + 1 : 1,
                            };
                        }

                        if (name) {
                            controlContexts[name] = undefined;
                        }
                    }

                    if ($dyn.value(headerContext.Visible)) {
                        headerMetadata.push(headerContext);
                    }

                    if (name) {
                        ///#DEBUG
                        if (listHeaderMetadata[name]) {
                            $dyn.logError('List: Duplicate control, name=%s', name);
                        }
                        ///#ENDDEBUG
                        listHeaderMetadata[name] = headerContext;
                    }
                });
            }

            lastHeaderMetadata = listHeaderMetadata;
            lastControlContexts = controlContexts;

            return headerMetadata;
        });

        self.RenderedHeaderMetadata = $dyn.observable();

        if (root[0]) {
            if (root[0].getAttribute('data-dyn-controlname')) {
                self.ListBoxIdentifier = "lb-" + root[0].getAttribute('data-dyn-controlname');
            }
        }

        self._rerenderOptions = $dyn.observable(0);
        self.rerender = function () {
            self._rerenderOptions($dyn.peek(self._rerenderOptions) + 1);
        };

        self._dataChangedVersion = $dyn.observable(0);
        self.dataChanged = function () {
            self._dataChangedVersion($dyn.peek(self._dataChangedVersion) + 1);
        };

        //observe column visiblity changes and rerender grid
        $dyn.observe(function () {
            var rowTemplates = element ? $dyn.value(element.dynChildren) : null;
            var columnVisibility = [];
            var firstRowColumns = null;
            if (rowTemplates && rowTemplates.length && rowTemplates.length > 0) {
                var rowtemplate = rowTemplates[0];
                var role = $dyn.role(rowtemplate);
                var context = $dyn.context(rowtemplate);
                // Elements can be added to dynChildren before they are data bound so take a dependency on databound.
                if (role && !role.isDataBound() && context) {
                    $dyn.log.list && $dyn.log.list("Role %o not databound yet", role);
                }
                firstRowColumns = $dyn.value(rowTemplates[0].dynChildren);
            }
            if (firstRowColumns && firstRowColumns.length) {
                for (var index = 0; index < firstRowColumns.length ; index++) {
                    var parentRole = $dyn.role(rowTemplates[0]);
                    var column = firstRowColumns[index];
                    var role = parentRole.getChildRole(column);
                    var context = $dyn.context(column);
                    // Elements can be added to dynChildren before they are data bound so take a dependency on databound.
                    if (!role.isDataBound() && context) {
                        $dyn.log.list && $dyn.log.list("Role %o at index %i for control %s not databound yet", role, index, column.getAttribute("data-dyn-controlname"));
                    }
                    if (context) {
                        columnVisibility.push($dyn.value(context.Visible));
                    }
                }
            }
            return columnVisibility;
        }, function (columnVisibility) {
            if (columnVisibility && columnVisibility.length > 0) {
                self.rerender();
            }

        });

        self._densityChangeHandler = function (evt, newDensity) {
            // Force the post render to redo the header layout when the list becomes visible again
            root.addClass('layoutPending');
            self.RowHeightInPixels = newDensity;
            self.ScrollbarSizeInPixels = newDensity * 2 / 3;
        };

        $(document).on("densitychanged", self._densityChangeHandler);

        self.IsNoResultsMessageDisplayed = $dyn.computed(function () {
            return $dyn.length(self.DataSource) === 0;
        });

        self.getNoRowIndicator = function() {
            return self._noRowsIndicator && self._noRowsIndicator.getElementsByClassName("list-No-Rows-IndicatorImage");
        };

        self.focusNoRowIndicator = function() {
            if (self._noRowsIndicator) {
                var image = self._noRowsIndicator.getElementsByClassName("list-No-Rows-IndicatorImage")[0];
                image && image.focus();
            }
        };

        self.HasRecords = $dyn.computed(function () {
            return $dyn.length(self.DataSource) > 0;
        });
        self.AllowHeaderFocus = $dyn.observable(false);

        self.NoResultImage = $dyn.shell.rootPath.concat("resources/images/emptyGrid.svg");
        self.ShowHeader = $dyn.computed(function () {
            return $dyn.value(self.Mode) === $dyn.ui.ListMode.tabular && $dyn.value(self.IsHeaderVisible);
        });

        /**
         * @function ArePagingButtonsVisible
         * @memberof $dyn.controls.List#
         * @internal
         * @summary Indicates whether paging buttons are visible
         * @returns {Boolean} True paging buttons are visible, false otherwise
         */
        self.ArePagingButtonsVisible = $dyn.computed(function () {
            var isVisible = true;
            if ($dyn.value(self.InfiniteScroll) || !$dyn.value(self.IsPagingSupported) || self.HasSinglePage()) {
                isVisible = false;
            }
            return isVisible;
        });

        var scrollRegion;
        var headerScrollRegion;
        var lastHorizontalScroll;
        self.SyncHorizontalScrollFromHeader = function () {
            /// <summary>
            /// Synchronizes the table's horizontal scroll offset with the header's horizontal scroll offset.
            /// </summary>

            if (!scrollRegion) {
                // Cache the header and scroll region
                scrollRegion = element.getElementsByClassName("scrollRegion")[0];
            }

            if (!headerScrollRegion) {
                headerScrollRegion = element.getElementsByClassName("listHeader")[0];
            }

            if (scrollRegion && headerScrollRegion) {
                var horizontalScroll = headerScrollRegion.scrollLeft;
                if (lastHorizontalScroll !== horizontalScroll) {
                    scrollRegion.scrollLeft = horizontalScroll;
                }

                lastHorizontalScroll = horizontalScroll;
            }
        };

        self.SyncHorizontalScrollOffset = function () {
            /// <summary>
            /// Synchronizes the header's horizontal scroll offset with the data table's horizontal scroll offset.
            /// </summary>

            if (!scrollRegion) {
                // Cache the header and scroll region
                scrollRegion = element.getElementsByClassName("scrollRegion")[0];
            }

            if (!headerScrollRegion) {
                headerScrollRegion = element.getElementsByClassName("listHeader")[0];
            }

            if (scrollRegion && headerScrollRegion) {
                var horizontalScroll = scrollRegion.scrollLeft;

                if (lastHorizontalScroll !== horizontalScroll) {
                    headerScrollRegion.scrollLeft = horizontalScroll;

                    $dyn.log.list && $dyn.log.list('List: Sync offset from %s to %s', lastHorizontalScroll, horizontalScroll);

                    // if there is a open grid header filter close it
                    var filteredColumn = root.find(".columnHeaderWrapper[aria-expanded='true']");
                    if (filteredColumn[0]) {
                        var context = $dyn.context(filteredColumn[0]);
                        if (context) {
                            filteredColumn[0].click();
                        }
                    }
                }

                lastHorizontalScroll = horizontalScroll;
            }
        };

        self.CalculateRowHeight = function () {
            // Use outerHeight to include row borders
            return root.find(".listView .listItem").first().outerHeight();
        };

        /**
         * @function GetSelectedRow
         * @memberof $dyn.controls.List#
         * @protected
         * @summary Gets selected row element
         * @returns {JQuery} Selected row element
         */
        self.GetSelectedRow = function () {
            /// <summary>
            /// Gets the currently selected row in the grid.
            /// </summary>
            return root.find('.listItem[data-dyn-row-active=true]');
        };

        self.headerLayoutTimer = null;
        self.incrementRenderCountOnHeaderLayout = false;
        self.performHeaderLayout = function (incrementRenderCount) {
            // if this was called from a vertical scroll, the layout is not necessary
            if ($dyn.value(self.ShowHeader) && !$dyn.value(self._dataLoadedRenderPending)) {
                root.find('.table_header').layoutFloatingTableHeader(root.find('.TabularView'));

                self.incrementRenderCountOnHeaderLayout = false;
            }
        };

        // Represents the internal post render
        self.postRenderInternal = function (onlyExecuteIfLayoutPending) {
            self.element.setAttribute('aria-rowcount', $dyn.length(self.DataSource));
            if (self.element && self.ArePagingButtonsVisible())
            {
                var $pagingButtons = $(self.element).find('.pagingButtons');
                if ($pagingButtons) {
                    $pagingButtons.css('width', self.GetScrollRegionWidth() + "px");
                }
            }
            if (root.closest("[data-dyn-lastvisible='false']").length > 0) {
                // Performance note: Don't use the jQuery :visible pseudo-selector here as it adds a large amount of overhead. Keep it light-weight
                // and handle the cases that we care about.

                // Post render layout can only run if the List is visible. The layout system will notify the List when
                // the next layout event occurs--at which point list will attempt layout again.

                if (!onlyExecuteIfLayoutPending) {
                    // Flag the control as having pending layout that cannot be completed.
                    root.addClass('layoutPending');
                }
            } else if (!onlyExecuteIfLayoutPending || root.hasClass('layoutPending')) {
                // The control is visible and is processing a pending layout or can have layout run immediately during the foreach render pass.
                root.removeClass('layoutPending');
                self.ApplyAutoSizing();
                if ($dyn.value(self.Mode) !== $dyn.ui.ListMode.tabular) {
                    self.ForEachRenderCount($dyn.value(self.ForEachRenderCount) + 1);
                } else {
                    // Note that for tabular its render count is incremented synchronously with header layout so that the
                    // header is not prematurely displayed to the user before rendering is completed.
                    self.performHeaderLayout(true);
                }

                // Call the public post render callback, if any
                $dyn.applyFunction(self.PostRender, self);
            }
        };

        self.getDataColumnContextsByIndex = function (index, onlyIncludeVisible) {
            var foundColumns = [];
            var rowTemplates = $dyn.value(element.dynChildren);
            if (rowTemplates.length) {
                jQuery.each(rowTemplates, function (rowTemplateIndex, rowTemplate) {
                    var rowColumns = $dyn.value(rowTemplate.dynChildren);
                    if (rowColumns) {
                        if (onlyIncludeVisible) {
                            rowColumns = rowColumns.filter(function(col) {
                                var colContext = $dyn.context(col);
                                return colContext && $dyn.value(colContext.Visible);
                            });
                        }
                        var foundColumn = rowColumns[index];
                    }

                    if (foundColumn) {
                        foundColumns.push($dyn.context(foundColumn));
                    }
                });
            }

            return foundColumns;
        };

        self.findControlElementsInAllRows = function (controlName) {
            return $('[data-dyn-controlname="' + controlName + '"]', root);
        };

        self.IsStaticRowHeightValid = function () {
            /// <summary>
            /// A static row height is used as a performance optimization in most cases (avoids an expensive height calculation; ~100ms). However, in some
            /// cases the static row height is not acceptable--such as in multi-line string edits and images.
            /// </summary>
            return root.find(".listItem").last().find(".customCellHeight").length === 0;
        };

        /**
         * @function OnScroll
         * @memberof $dyn.controls.List#
         * @private
         * @summary List 'scroll' handler
         * @param {event} event - 'scroll' event
         */
        self.OnScroll = function (event) {
            self.SyncHorizontalScrollOffset();
            if (self._scrollHandler) {
                self._scrollHandler(event);
            } else {
               // if infiniteScroll=true, then the _scrollHandler calls this method for us
               self.UpdateScrollTop();
            }
            if (self._onScrollHandled) {
                self._onScrollHandled();
            }
        };

        /**
         * @function RowClicked
         * @memberof $dyn.controls.List#
         * @private
         * @summary List row 'click' handler
         * @param {event} event - 'click' event
         */
        self.RowClicked = function (event) {
            self._renderer.setUserActionBusy();
            self._rowOperationsManager.RowClicked(event);
        };

        /**
         * @function RowKeyDown
         * @memberof $dyn.controls.List#
         * @private
         * @summary List 'keydown' handler
         * @param {event} event - 'keydown' event
         */
        self.RowKeyDown = function (event) {
            $dyn.log.list && $dyn.log.list("List: Row Key Down");
            self._renderer.setUserActionBusy();
            $dyn.util.handleShortcuts(this, event);

            //Ignore any further shortcuts, even if we didn't handle it
            if (self._surroundingBlockingDivShown && event.originalEvent && event.originalEvent.IgnoreShortcuts !== false) {
                event.originalEvent.IgnoreShortcuts = true;
            }
        };

        /**
        * @function RowKeyUp
        * @memberof $dyn.controls.List#
        * @private
        * @summary List 'keyup' handler
        * @param {event} event - 'keyup' event
        */
        self.RowKeyUp = function (event) {
            if ($dyn.util.isShortcutKeyPressed(event, { alt: false, keyCode: $dyn.ui.KeyCodes.downArrow })) {
                self.downArrowKeyUpTriggered = true;
            }
        };

        /// <summary>
        /// Handles all key down events while there's a pending row change interaction.
        /// This method will update various internal grid variables that will be used after
        /// the row change interaction has been processed
        /// </summary>
        self.BlockingDivAllowedKeyDown = function (event) {
            $dyn.log.list && $dyn.log.list("List: Entered BlockingDivAllowedKeyDown handler");
            self.RowKeyDown(event);
            $dyn.log.list && $dyn.log.list("List: Leaving BlockingDivAllowedKeyDown handler");
        };

        // Observable that will be toggled when the grid's internal contents height has been overridden
        self.HeightOverride = $dyn.observable(false);
        self.CachedScrollRegionHeight;
        self.CachedScrollRegionTop;
        self.scrollRegion;

        self.WindowResizeHandler = function () {
            self.UpdateScrollRegionWidth();
            self.UpdateScrollRegionHeight();
            self._reevaluateSurroundingBlockingDiv();
            if (self._resizeHandler) {
                self._resizeHandler();
            }
        };

        self.ApplyAutoSizing = function () {
            var scrollRegion = self.GetScrollRegion();
            if (scrollRegion) {
                $dyn.observe($dyn.options(function () { 
                    return { 
                        layoutHeight: self.LayoutHeight, 
                        visibleRows: self.VisibleRowCount, 
                        rowHeight: self.RowHeightInPixels,
                        scrollbarSize: self.ScrollbarSizeInPixels,
                        override: self.HeightOverride 
                    }; 
                }, $dyn.ui.updating), function (options) {
                        var layoutHeightToLower = options.layoutHeight.toLowerCase();
                        var heightOverride = options.override;
                        if ((scrollRegion.__layoutHeight && scrollRegion.__layoutHeight !== layoutHeightToLower)
                            || (scrollRegion.__layoutHeightOverride && scrollRegion.__layoutHeightOverride !== heightOverride)) {
                            scrollRegion.style.height = null;
                        }
                        else {
                            scrollRegion.__layoutHeight = layoutHeightToLower;
                            scrollRegion.__layoutHeightOverride = heightOverride;
                        }
                        // If heightOverride is true, the grid should behave as SizeToContent height, and use the visibleRows option.
                        var visRows = (layoutHeightToLower != "auto" && layoutHeightToLower != "sizetocontent" && !heightOverride) ? 0 : options.visibleRows;
                        var userDensity = $dyn.densityThemeManager._defaultDensity;
                        if ($dyn.user) {
                            var userInfo = $dyn.user();
                            if (userInfo && userInfo.Density) {
                                userDensity = $dyn.value(userInfo.Density);
                            }
                        }

                        var rowHeight = (options.rowHeight || userDensity) + 3 /* 3px for margins */;
                        var scrollbarSize = options.scrollbarSize || userDensity * 2 / 3;
                        if (!self.IsStaticRowHeightValid() || ($dyn.value(self.Mode) === $dyn.ui.ListMode.list)) {
                            // The statically defined row height cannot be used because there are controls that span
                            // more than a single "normal" row in height.
                            var lastListItem = root.find(".listItem").last();
                            if (lastListItem.length > 0) {
                                rowHeight = lastListItem[0].offsetHeight;
                            }
                        }
                        if (visRows > 0 && rowHeight != 0) {
                            self.SetScrollRegionHeight(rowHeight * visRows + scrollbarSize);
                        }
                });

                //$dyn.observe($dyn.options(function () { return { visibleColumns: self.VisibleColumnCount }; }, $dyn.ui.updating), function (options) {
                   // if (options.visibleColumns > 0) {
                        //Visiblecolumns property does not align with current form patterns having SizeToAvaialble as the sizing
                        //Disable this property until the property specs are finalized or property is deprecated officially
                        //var width = 0;
                        //var columnCount = 0;
                        //if ($dyn.value(self.Mode) === $dyn.ui.ListMode.tabular)
                        //{
                        //    var headers = tableHeader.find(".columnHeader:not(.repeater-template)");
                        //    for (var i = 0 ; i < headers.length ; i++) {
                        //        if (columnCount >= options.visibleColumns) {
                        //            break;
                        //        }
                        //        var colWidth = $(headers[i]).outerWidth();
                        //        if (colWidth > 0) {
                        //            width += colWidth;
                        //            columnCount = columnCount + 1;
                        //        }

                        //    };

                        //    if (root.attr("data-dyn-hidemarkcolumn") === undefined || root.attr("data-dyn-hidemarkcolumn") === true) {
                        //        width += tableHeader.find(".markAllContainer").outerWidth();
                        //    }

                        //    if (width > 0) {
                        //        root.width(width);
                        //    }
                        //}
                    //}
                //});
            }
        };
        self.GetFirstMarkedRow = function () {
            var markedIndexes = self.MarkedIndexes();
            if (!markedIndexes) {
                return undefined;
            }

            for (var idx = 0; idx < markedIndexes.length; ++idx) {
                if (markedIndexes[idx]) {
                    return idx;
                }
            }

            return undefined;
        };
        self.GetLastMarkedRow = function () {
            var markedIndexes = self.MarkedIndexes();
            if (!markedIndexes) {
                return undefined;
            }

            for (var idx = markedIndexes.length - 1; idx >= 0; --idx) {
                if (markedIndexes[idx]) {
                    return idx;
                }
            }

            return undefined;
        };
        self.GetPreviousMarkedRow = function () {
            var markedIndexes = self.MarkedIndexes();
            if (!markedIndexes) {
                return undefined;
            }
            var activeRowIdx = self.getActiveRowClientIndex();
            var idx = activeRowIdx - 1;
            // We want it to be cyclic (from the first to the last)
            while (idx != activeRowIdx) {
                if (markedIndexes[idx]) {
                    return idx;
                }
                --idx;
                if (idx < 0) {
                    idx = markedIndexes.length - 1;
                }
            }

            return undefined;
        };
        self.GetNextMarkedRow = function () {
            var markedIndexes = self.MarkedIndexes();
            if (!markedIndexes) {
                return undefined;
            }

            var activeRowIdx = self.getActiveRowClientIndex();

            var idx = activeRowIdx + 1;
            // We want it to be cyclic (from the last to the first)
            while (idx != activeRowIdx) {
                if (markedIndexes[idx]) {
                    return idx;
                }
                ++idx;
                if (idx >= markedIndexes.length) {
                    idx = 0;
                }
            }

            return undefined;
        };
    };

    $dyn.controls.List.prototype = $dyn.ui.extendPrototype($dyn.ui.Control.prototype, {
        IsSelectionIndicatorVisible: true,
        IsHeaderVisible: true,
        //  DataSource is an array of object (not necessarily an array of $dyn.serverForm.Model)
        DataSource: null,
        FastEdit: false,
        NavigationEnabled: false,
        // ForcePrimaryNavigation means that the grid should perform its NavigationAction on row selection.
        ForcePrimaryNavigation: false,
        NavigationAction: null,
        AllowCreateOnKeyDown: undefined,
        ShowHeader: true,
        ShowSelection: true,

        /*SelectedIndex is copied from the FormDataSource in html-binding-serverform.js*/
        SelectedIndex: undefined,
        // The cache related index that indicates the starting index of the fetched data (page)
        FetchedDataStartPosition: 0,
        // The current size of the cache
        CacheSize: 0,
        // Indicates whether we have data above starting position of the cache
        DataAbove: false,
        // indicates whether we have data below ending position of the cache
        DataBelow: false,

        Virtualized: false,
        LoadPreviousPage: null,
        LoadNextPage: null,
        ExportAllowed: true,
        ExportLabel: "",
        ExportToExcel: null,
        MarkAllRecords: false,
        // Array valued
        MarkedIndexes: null,
        IsMarkingEnabled: true,
        RowHeightInPixels: null,
        ScrollbarSizeInPixels: null,
        VisibleRowCount: 0,
        VisibleColumnCount: 0,
        Mode: $dyn.ui.ListMode.tabular,
        IsPagingSupported: true,
        //set to true when ScrolIntoView to a row after grid render is not desired
        doNotScrollIntoView: false,
        // Indicates whether grid is in the infinite scroll mode
        InfiniteScroll: false,
        ForceInvisibleRender: false,
        // Field to group the grid with
        GroupBy: "",
        PreRender: null,
        PostRender: null,

        // function passed to grid by the grid consumer to get the item version for partial rendering scenarios.
        // This function should take in the item object and return the item(/model) version.
        GetDataVersion: null,

        // function passed to grid by the grid consumer to get the item id for partial rendering scenarios.
        // This function should take in the item object and return the item identifier.
        GetDataId: null,
        AsyncRender: true,
        ShowRowLabels: false,
        GridLines: $dyn.ui.GridLines.Auto,
        OriginatingType: "",

        // Private variables
        _selectedRow: undefined,
        _rangePivot: null, // Stores the index of the pivot record around which range is calculated. The index is cache-related.
        _initialDataStartPosition: null, // FetchedDataStartPosition used to compute the row index for ChangeSelectedIndex
        _rangeSelection: false, // Flag indicating that rangeSelection is ON and when that is happening, rangePivot is not reset.
        _markingAction: null, // Stores the marking operation done on the list
        _densityChangeHandler: null, // Stores the density change event handler
        // The cache related index (0-based) of the first rendered row
        clientCacheStart: undefined,
        // Last position where from client cache start was updated
        clientLastStartPosition: undefined,
        // Last position where from load data was invoked
        clientLastEndPosition: undefined,
        // Scroll direction from the last time load data was invoked
        prevScrollDirection: $dyn.ui.ScrollDirection.none,
        //Set to true if arrow key down is triggered from row
        arrowKeyDownTriggered: undefined,
        //Set to true if down arrow key up is triggered from row
        downArrowKeyUpTriggered: undefined,
        //merge direction, true if scrolling direction is upwards
        mergeAbove: undefined,
        //merge direction, true if scrolling direction is downwards
        mergeBelow: undefined,
        // Represents the fraction of viewport size (amount of rows that can be fitted into viewport)
        // that is alowed to render before discarding old rows
        _RowCountThresholdFraction: 8,

        //whether or not to check for visible (offsetParent == null) when calculating scroll region height
        _skipOffsetParentCheck: false,

        //whether or not a render got triggered while the grid was not displayed
        _renderTriggeredWhileHidden: false,
        
        //Control which has the default action behavior
        _defaultActionControl: undefined,

        //Keyboard or mouse
        InputMode: null,

        properties: {
            // When true the active row selection visualization is not displayed
            HideSelection: {
                defaultValue: false,

                serialize: true,
                invokeHandlers: false,
            },
        },

        _tabFocusing: false,
        _tabFocused: false,

        _reevaluateElements: false,

        // Whether or not to update the data context when switching rows
        // NOTE: Should only be used by client-side only infinite scroll grids
        // Serverform implmentation does not need to do this because it is bound to the activeRecordModel
        // so the model's observables already get updated when the SelectedIndex on the collection changes
        UpdateActiveItemBindings: false,

        OverrideNoResultsMessage: undefined,
        getNoResultsMessage: function() {
            var message = "";
            if (this.OverrideNoResultsMessage !== undefined && this.OverrideNoResultsMessage !== null) {
                message = $dyn.value(this.OverrideNoResultsMessage);
            } else {
                message = $dyn.label('List_NoResultsMessage');
            }

            return message;
        },
        
        /**
        @memberof $dyn.controls.List#
        @summary Gets common context menu options for list
        @function
        @public
        @return {Array} An array of Objects, each representing an option in
        the context menu.
        */
        getCommonGridContextMenuOptions: function () {
            var options = [];
            if (this.ExportToExcel && $dyn.value(this.ExportAllowed)) {
                var exportOption = {
                    Label: $dyn.label("ContextMenu_ExportAllRows"),
                    Command: this.ExecuteExportToExcel.bind(this, false)
                };
                options.push(exportOption);

                if ($dyn.callFunction(this.hasMarkedRows, this)) {
                    exportOption = {
                        Label: $dyn.label("ContextMenu_ExportMarkedRows"),
                        Command: this.ExecuteExportToExcel.bind(this, true)
                    };
                    options.push(exportOption);
                }
            }
            return options;
        },

        /**
        @memberof $dyn.controls.List#
        @summary Gets context menu options for list
        @function
        @public
        @return {Array} An array of Objects, each representing an option in
        the context menu.
        */
        getContextMenuOptions: function () {
            var options = this.getCommonGridContextMenuOptions();
            Array.prototype.push.apply(options, $dyn.ui.Control.prototype.getContextMenuOptions.apply(this));
            return options;
        },

        /**
        * Short cut handlers for Grid
        * FLOW FOR SELECTION AND MARKING
        * 1) User navigates through the grid using keyboard
        * 2) Based on the key being used, the FocusRowRelativeToActiveRow function is called with the correct set of parameters.
        * 3a) FocusRowRelativeToActiveRow calculates the index of the row that needs to be focused on based on the parameters passed to it.
        * 3b) Any control changes are processed before processing row change
        * 3c) If the client is processing interactions during this time we block and wait
        * 3d) Once the client is free, we check if there are any invalid controls on the form.
        * 3e) If there are no invalid controls, the selection visualization is updated. - New row is seen as selected and old row is seen as unselected.
        * 3f) Calculate the markMode. i.e. whether it is a single row selection, range selection, toggling or marked records
        * 3g) If there are no modifier keys associated with the command and there is defaultAction/jumpRef action on the control then send the secondary navigation command to the server
        * 3h) Call FocusRowAndSelectCell which sends the ChangeSelectedIndex to the server with a callback, via other grid components viz. DataProvider and SyncManager
        * 4ai) If changeRow on the server succeeds then ChangeSelectedIndex command has a return value of 1.
        * 4aii) SelectedIndexSubscription is called as the SelectedIndex is updated by the server. This function switches the current active row to be inactive and makes the row at the new selected index as active.
        * 4aiii) Ensures that the visualization controlled by aria-selected and aria-checked is correct on the active row.
        * 4aiv) MarkedIndexes array that stores which records on the grid are marked is filled only after the server returns.
        * 4bi) If changeRow on the server fails then ChangeSelectedIndex command has a return value of 0.
        * 4bii) Callback associated with the ChangeSelectedIndex is invoked which reverses the aria-checked and aria-selected attributes on the records to ensure that the previous record has focus.
        */
        Shortcuts: $dyn.extendPrototype($dyn.ui.Control.prototype.Shortcuts, {
            DownArrow: {
                Keys: { keyCode: $dyn.ui.KeyCodes.downArrow },
                Handler: function (event) {
                    if ($dyn.value(this.Enabled) && !$dyn.value(this.AllowHeaderFocus)) {
                        var inputMode = this.InputMode = $dyn.ui.InputType.keyboard;
                        var markMode = this._calculateMarkMode(event);
                        this.FocusRowRelativeToActiveRow(1, markMode, inputMode, event, true);
                        return true;
                    }
                    return false;
                },
                ShowToUser: function () { return $dyn.value(this.Enabled) && !$dyn.value(this.AllowHeaderFocus); },
                Description: function () { return this._getListRowChangeShortCutDescription(this, $dyn.controls.List.prototype.Shortcuts.DownArrow.Keys); }
            },
            ShiftDownArrow: {
                Keys: { shift: true, keyCode: $dyn.ui.KeyCodes.downArrow },
                Handler: function (event) {
                    if ($dyn.value(this.Enabled) && !$dyn.value(this.AllowHeaderFocus)) {
                        var inputMode = this.InputMode = $dyn.ui.InputType.keyboard;
                        var markMode = this._calculateMarkMode(event);
                        this.FocusRowRelativeToActiveRow(1, markMode, inputMode, event, true);
                        return true;
                    }
                    return false;
                },
                ShowToUser: function () { return $dyn.value(this.Enabled) && !this._isLastRow() && !$dyn.value(this.AllowHeaderFocus); },
                Description: function () { return this._getListRowChangeShortCutDescription(this, $dyn.controls.List.prototype.Shortcuts.ShiftDownArrow.Keys); }
            },
            CtrlDownArrow: {
                Keys: { ctrl: true, keyCode: $dyn.ui.KeyCodes.downArrow },
                Handler: function (event) {
                    if ($dyn.value(this.Enabled) && !$dyn.value(this.AllowHeaderFocus)) {
                        var inputMode = this.InputMode = $dyn.ui.InputType.keyboard;
                        var markMode = this._calculateMarkMode(event);
                        this.FocusRowRelativeToActiveRow(1, markMode, inputMode, event, true);
                        return true;
                    }
                    return false;
                },
                ShowToUser: function () { return $dyn.value(this.Enabled) && !this._isLastRow() && !$dyn.value(this.AllowHeaderFocus); },
                Description: function () { return this._getListRowChangeShortCutDescription(this, $dyn.controls.List.prototype.Shortcuts.CtrlDownArrow.Keys); }
            },
            CtrlShiftDownArrow: {
                Keys: { ctrl: true, shift: true, keyCode: $dyn.ui.KeyCodes.downArrow },
                Handler: function (event) {
                    if ($dyn.value(this.Enabled) && !$dyn.value(this.AllowHeaderFocus)) {
                        var inputMode = this.InputMode = $dyn.ui.InputType.keyboard;
                        var markMode = this._calculateMarkMode(event);
                        this.FocusRowRelativeToActiveRow(1, markMode, inputMode, event, true);
                        return true;
                    }
                    return false;
                },
                ShowToUser: false
            },
            UpArrow: {
                Keys: { keyCode: $dyn.ui.KeyCodes.upArrow },
                Handler: function (event) {
                    if ($dyn.value(this.Enabled) && !$dyn.value(this.AllowHeaderFocus)) {
                        var inputMode = this.InputMode = $dyn.ui.InputType.keyboard;
                        var markMode = this._calculateMarkMode(event);
                        this.FocusRowRelativeToActiveRow(-1, markMode, inputMode, event, true);
                        return true;
                    }
                    return false;
                },
                ShowToUser: function () { return $dyn.value(this.Enabled) && !this._isFirstRow() && !$dyn.value(this.AllowHeaderFocus); },
                Description: function () { return this._getListRowChangeShortCutDescription(this, $dyn.controls.List.prototype.Shortcuts.UpArrow.Keys); }
            },
            ShiftUpArrow: {
                Keys: { shift: true, keyCode: $dyn.ui.KeyCodes.upArrow },
                Handler: function (event) {
                    if ($dyn.value(this.Enabled) && !$dyn.value(this.AllowHeaderFocus)) {
                        var inputMode = this.InputMode = $dyn.ui.InputType.keyboard;
                        var markMode = this._calculateMarkMode(event);
                        this.FocusRowRelativeToActiveRow(-1, markMode, inputMode, event, true);
                        return true;
                    }
                    return false;
                },
                ShowToUser: function () { return $dyn.value(this.Enabled) && !this._isFirstRow() && !$dyn.value(this.AllowHeaderFocus); },
                Description: function () { return this._getListRowChangeShortCutDescription(this, $dyn.controls.List.prototype.Shortcuts.ShiftUpArrow.Keys); }
            },
            CtrlUpArrow: {
                Keys: { ctrl: true, keyCode: $dyn.ui.KeyCodes.upArrow },
                Handler: function (event) {
                    if ($dyn.value(this.Enabled) && !$dyn.value(this.AllowHeaderFocus)) {
                        var inputMode = this.InputMode = $dyn.ui.InputType.keyboard;
                        var markMode = this._calculateMarkMode(event);
                        this.FocusRowRelativeToActiveRow(-1, markMode, inputMode, event, true);
                        return true;
                    }
                    return false;
                },
                ShowToUser: function () { return $dyn.value(this.Enabled) && !this._isFirstRow() && !$dyn.value(this.AllowHeaderFocus); },
                Description: function () { return this._getListRowChangeShortCutDescription(this, $dyn.controls.List.prototype.Shortcuts.CtrlUpArrow.Keys); }
            },
            CtrlShiftUpArrow: {
                Keys: { ctrl: true, shift: true, keyCode: $dyn.ui.KeyCodes.upArrow },
                Handler: function (event) {
                    if ($dyn.value(this.Enabled) && !$dyn.value(this.AllowHeaderFocus)) {
                        var inputMode = this.InputMode = $dyn.ui.InputType.keyboard;
                        var markMode = this._calculateMarkMode(event);
                        this.FocusRowRelativeToActiveRow(-1, markMode, inputMode, event, true);
                        return true;
                    }
                    return false;
                },
                ShowToUser: false
            },
            AltShiftMF: {
                ChordKeys: {  alt: true, shift: true, keyCode: $dyn.ui.KeyCodes.letterM },
                Keys: { keyCode: $dyn.ui.KeyCodes.letterF },
                Handler: function (event) {
                    if (!$dyn.value(this.AllowHeaderFocus)) {
                        var inputMode = this.InputMode = $dyn.ui.InputType.keyboard;
                        var markMode = $dyn.controls.List.MarkMode.none;
                        var firstMarkedRow = this.GetFirstMarkedRow();
                        if (firstMarkedRow !== undefined) {
                            this.FocusRow(firstMarkedRow, markMode, inputMode, event, true);
                        }

                        return true;
                    }
                    return false;
                },
                Description: $dyn.label('Grid_MoveToFirstMarkedRowShortCut'),
                Category: $dyn.ui.ShortcutCategory.Navigate
            },
            AltShiftML: {
                ChordKeys: {  alt: true, shift: true, keyCode: $dyn.ui.KeyCodes.letterM },
                Keys: { keyCode: $dyn.ui.KeyCodes.letterL },
                Handler: function (event) {
                    if (!$dyn.value(this.AllowHeaderFocus)) {
                        var inputMode = this.InputMode = $dyn.ui.InputType.keyboard;
                        var markMode = $dyn.controls.List.MarkMode.none;
                        var lastMarkedRow = this.GetLastMarkedRow();
                        if (lastMarkedRow !== undefined) {
                            this.FocusRow(lastMarkedRow, markMode, inputMode, event, true);
                        }

                        return true;
                    }
                    return false;
                },
                Description: $dyn.label('Grid_MoveToLastMarkedRowShortCut'),
                Category: $dyn.ui.ShortcutCategory.Navigate
            },
            AltShiftMN: {
                ChordKeys: {  alt: true, shift: true, keyCode: $dyn.ui.KeyCodes.letterM },
                Keys: { keyCode: $dyn.ui.KeyCodes.letterN },
                Handler: function (event) {
                    if (!$dyn.value(this.AllowHeaderFocus)) {
                        var inputMode = this.InputMode = $dyn.ui.InputType.keyboard;
                        var markMode = $dyn.controls.List.MarkMode.none;
                        var nextMarkedRow = this.GetNextMarkedRow();
                        if (nextMarkedRow !== undefined) {
                            this.FocusRow(nextMarkedRow, markMode, inputMode, event, true);
                        }

                        return true;
                    }
                    return false;
                },
                Description: $dyn.label('Grid_MoveToNextMarkedRowShortCut'),
                Category: $dyn.ui.ShortcutCategory.Navigate
            },
            AltShiftMP: {
                ChordKeys: {  alt: true, shift: true, keyCode: $dyn.ui.KeyCodes.letterM },
                Keys: { keyCode: $dyn.ui.KeyCodes.letterP },
                Handler: function (event) {
                    if (!$dyn.value(this.AllowHeaderFocus)) {
                        var inputMode = this.InputMode = $dyn.ui.InputType.keyboard;
                        var markMode = $dyn.controls.List.MarkMode.none;
                        var previousMarkedRow = this.GetPreviousMarkedRow();
                        if (previousMarkedRow !== undefined) {
                            this.FocusRow(previousMarkedRow, markMode, inputMode, event, true);
                        }

                        return true;
                    }
                    return false;
                },
                Description: $dyn.label('Grid_MoveToPreviousMarkedRowShortCut'),
                Category: $dyn.ui.ShortcutCategory.Navigate
            },
            PageDown: {
                Keys: { keyCode: $dyn.ui.KeyCodes.pageDown },
                Handler: function (event) {
                    if ($dyn.value(this.Enabled) && !$dyn.value(this.AllowHeaderFocus)) {
                        var inputMode = this.InputMode = $dyn.ui.InputType.keyboard;
                        var markMode = this._calculateMarkMode(event);
                        this.FocusRowRelativeToActiveRow(this.CalculateNumberOfVisibleRows(), markMode, inputMode, event);
                        return true;
                    }
                    return false;
                },
                ShowToUser: function () { return $dyn.value(this.Enabled) && !this._isLastRow() && !$dyn.value(this.AllowHeaderFocus); },
                Description: $dyn.label('Grid_NextPageShortcut')
            },
            PageUp: {
                Keys: { keyCode: $dyn.ui.KeyCodes.pageUp },
                Handler: function (event) {
                    if ($dyn.value(this.Enabled) && !$dyn.value(this.AllowHeaderFocus)) {
                        var inputMode = this.InputMode = $dyn.ui.InputType.keyboard;
                        var markMode = this._calculateMarkMode(event);
                        this.FocusRowRelativeToActiveRow(-this.CalculateNumberOfVisibleRows(), markMode, inputMode, event);
                        return true;
                    }
                    return false;
                },
                ShowToUser: function () { return $dyn.value(this.Enabled) && !this._isFirstRow() && !$dyn.value(this.AllowHeaderFocus); },
                Description: $dyn.label('Grid_PreviousPageShortcut')
            },
            ControlSpace: {
                Keys: { ctrl : true, keyCode: $dyn.ui.KeyCodes.spacebar },
                Handler: function (event) {
                    if ($dyn.value(this.Enabled) && !$dyn.value(this.AllowHeaderFocus)) {
                        this._executeRowMarkUpdate();             
                        return true;
                    }
                    return false;
                },
                RecordTelemetry: true,
                ShowToUser: function () { return $dyn.value(this.Enabled) && !$dyn.value(this.AllowHeaderFocus); },
                Description: function () {
                    var shortCutDescription = $dyn.label('Grid_CurrentRowSelectionShortcut');
                    var marked = this.IsMarked($dyn.value(this.SelectedIndex));
                    if (marked) {
                        shortCutDescription = $dyn.label('Grid_CurrentRowClearShortcut');
                    }
                    return shortCutDescription;
                }
            },
            ControlShiftM: {
                Keys: { ctrl: true, shift: true, keyCode: $dyn.ui.KeyCodes.letterM },
                Handler: function (event) {
                    if ($dyn.value(this.Enabled) && !$dyn.value(this.AllowEdit)) {
                        this.ToggleMarkAllRecordsMode();
                        return true;
                    }
                    return false;
                },
                ShowToUser: function () { return $dyn.value(this.Enabled) && !$dyn.value(this.AllowEdit); },
                Description: function () {
                    var shortCutDescription = $dyn.label('Grid_SelectAllRowsShortcut');
                    var marked = this.hasMarkedRows();
                    if (marked) {
                        shortCutDescription = $dyn.label('Grid_ClearAllRowsShortcut');
                    }
                    return shortCutDescription;
                }
            },
            Enter: {
                Keys: { keyCode: $dyn.ui.KeyCodes.enter },
                Handler: function (event) {
                    return this._performNonEditModeNavigationAction(event);
                },
                ShowToUser: false
            },
            ControlG: {
                Keys: { ctrl: true, keyCode: $dyn.ui.KeyCodes.letterG },
                Handler: function (event) {
                    var $activeElement = this.FindActiveElement();
                    if ($activeElement) {
                        var dynControl = $dyn.context($activeElement[0]);
                        if (dynControl) {
                            var columnHeaderName = dynControl.Name;
                            if (dynControl.ReferenceGroup) {
                                columnHeaderName = dynControl.ReferenceGroup.Name;
                            }
                            var $activeColumnHeader = $(selector().attr('data-dyn-columnname', columnHeaderName).find(this.element)).find('.columnHeaderWrapper');
                            if ($activeColumnHeader) {
                                $activeColumnHeader.click();
                                $dyn.log.list && $dyn.log.list('Event propagation stopped for ctrl+g inside grid');
                                return true;
                            }
                        }
                    }
                    return false;
                },
                ShowToUser: true,
                Description: $dyn.label('Grid_OpenGridFilterShortCut'),
                Category: $dyn.ui.ShortcutCategory.Navigate
            },
            AltN: {
                Keys: { alt: true, keyCode: $dyn.ui.KeyCodes.letterN },
                Handler: function (event) {
                    var targetRowElement = $(event.target).closest('.rowTemplate')[0];
                    return this._newRecord(targetRowElement);
                },
                ShowToUser: false
            },
            Tab: {
                Keys: { keyCode: $dyn.ui.KeyCodes.tab },
                Handler: function (event) {
                    this._onTabKey(event);
                    return false;
                },
                RecordTelemetry: false,
                ShowToUser: false
            },
            ShiftTab: {
                Keys: { shift: true, keyCode: $dyn.ui.KeyCodes.tab },
                Handler: function (event) {
                    this._onTabKey(event);
                    return false;
                },
                RecordTelemetry: false,
                ShowToUser: false
            },
            AltF9: {
                Keys: { alt: true, keyCode: $dyn.ui.KeyCodes.f9 },
                Handler: function (event) {
                    var targetRowElement = $(event.target).closest('.listItem')[0];
                    return this._deleteRecord(targetRowElement, event);
                },
                ShowToUser: false
            },
            AltShiftH: {
                Keys: { alt: true, shift: true, keyCode: $dyn.ui.KeyCodes.letterH },
                Handler: function (event) {
                    if ($(event.target).parents().is('.listHeader')) {
                        this.AllowHeaderFocus(false);
                        if ($dyn.value(this.IsNoResultsMessageDisplayed)) {
                            this.focusNoRowIndicator();
                        } else {
                            this._renderer.focusCellInActiveRow();
                        }
                    } else {
                        var currentColumn = this._getParentControlName(event.target);
                        var headerColumn;
                        if (currentColumn !== this.Name) {
                            headerColumn = $.find("[data-dyn-columnname='" + currentColumn + "'] > .columnHeaderWrapper", this._headerRowElement);
                        }

                        if (!headerColumn || headerColumn.length <= 0) {
                            headerColumn = $.find(".columnHeaderWrapper:not(.markingColumnHeader)", this._headerRowElement);
                        }

                        if (headerColumn && headerColumn.length) {
                            this.AllowHeaderFocus(true);
                            this._headerTabbingContainerDispose = $dyn.util.tabbingContainer(this._headerRowElement, $dyn.label('ListHeader_RegionTitle'));
                            $dyn.log.focus && $dyn.log.focus("Focusing header: " + currentColumn);
                            headerColumn[0].focus();
                        }
                    }
                },
                ShowToUser: true,
                Description: function () { return $dyn.value(this.AllowHeaderFocus) ? $dyn.label('Grid_FocusActiveRowShortcut') : $dyn.label('Grid_FocusColumnHeaderShortCut'); },
                Category: $dyn.ui.ShortcutCategory.Navigate
            }
        }),        

        get allowedElements() {
            // Allow the grid, message bar, and any related add or delete buttons to not be covered by the blocking divs in rails mode
            if (this._reevaluateElements || !this._allowedElements || !this._allowedElements.length) {
                this._allowedElements = [this.element];
                if (this.Root && this.Root.MessageBar) {
                    if (this.Root.MessageBar.IsVisible()) {
                        // Only add message bar element if it is visible, but need the animation events either way
                        this._allowedElements.push(this.Root.MessageBar.element);
                    }
                    var self = this;
                    if ($dyn.flags.disableAnimations) {
                        //if no animations, then just observe expanded/visible observables
                        if (!this._msgBarComputed) {
                            this._msgBarComputed = $dyn.computed(function () {
                                self.Root.MessageBar.IsVisible();
                                self.Root.MessageBar.InfoExpanded();
                                self.Root.MessageBar.WarningExpanded();
                                self.Root.MessageBar.ErrorExpanded();
                            });
                            this._msgBarObserver = $dyn.observe(this._msgBarComputed, function () {
                                if (self._reevaluateTimeout) {
                                    self._reevaluateTimeout.cancel();
                                }
                                self._reevaluateTimeout = $dyn.async(function () {
                                    self._reevaluateSurroundingBlockingDiv();
                                });
                            });
                        }
                    } else {
                        //if animations, then listen for the animations to stop as to when to calculate the blocking divs
                        var msgBarAnimationEnd = function (event) {
                            if (event.propertyName != "color") {
                                self.Root.MessageBar.element.removeEventListener("transitionend", msgBarAnimationEnd, false);
                                self.Root.MessageBar.element.removeEventListener("animationend", msgBarAnimationEnd, false);
                                self.Root.MessageBar.element.removeEventListener("webkitAnimationEnd", msgBarAnimationEnd, false);
                                self._reevaluateSurroundingBlockingDiv();
                            }
                        };

                        this.Root.MessageBar.element.addEventListener("transitionend", msgBarAnimationEnd, false);
                        this.Root.MessageBar.element.addEventListener("animationend", msgBarAnimationEnd, false);
                        this.Root.MessageBar.element.addEventListener("webkitAnimationEnd", msgBarAnimationEnd, false);
                    }
                }

                if(this.Root && this.Root.element) {
                    var buttonElements = $(this.Root.element).find('[data-dyn-role="CommandButton"]');
                    if (buttonElements && buttonElements.length) {
                        for (var i = 0; i < buttonElements.length; i++) {
                            var button = $dyn.context(buttonElements[i]);
                            // Add or Delete buttons with the same service boundary
                            if (button && (button.Command === "DeleteRecord" || button.Command === "New") && $dyn.value(button.ServiceBoundary) === $dyn.value(this.ServiceBoundary)) {
                                this._allowedElements.push(buttonElements[i]);
                            }
                        }
                    }
                }

                this._reevaluateElements = false;
            }

            return this._allowedElements;
        },

        get SurroundingBlockingDiv() {
            if (!this._surroundingBlockingDiv) {
                var self = this;
                var clickHandler = function () {
                    self.SurroundingBlockingDiv.addClass("fadeInQuick");
                    self.SurroundingBlockingDiv.addClass("fastEditRailsMode");
                };
                this._surroundingBlockingDiv = new $dyn.shell.multiElementSurroundingBlockingDiv(clickHandler);
            }

            return this._surroundingBlockingDiv;
        },

        get IsRendering() {
            return this._renderer && this._renderer._isRendering;
        },

        /**
           @memberof $dyn.controls.List#
           @summary Adds columns to list
           @function
           @public
       */
        addColumns: function () {
            //We always want the focus to go to header when we finish add column
            //AllowHeaderFocus(true) will set focus to header via findFieldByName and findFieldByNameInServerSelectedRow methods in Grid
            //AllowHeaderFocus is set to false on focus out of column header
            //SelectForAdd command will open the add dialog, adding fields from that dialog will set focus to added control from X++
            //We will then put the focus to column header of that control
            //Cancel from the add dialog will have the focus on the column header where from add columns were clicked.
            this.AllowHeaderFocus(true);
            $dyn.callFunction(this.ExecutePersonalizationCommand, this, ["SelectForAdd"]);
        },

        // The row index used by ChangeSelectedIndex is in a volatile coordinate system, check the
        // data start position as late as possible to recalculate the row index if necessary
        recalculateRowIndex: function (initialRowIndex) {
            var currentDataStartPosition = $dyn.value(this.FetchedDataStartPosition);
            if (this._initialDataStartPosition == currentDataStartPosition) {
                return initialRowIndex;
            }

            var newRowIndex = initialRowIndex + this._initialDataStartPosition - currentDataStartPosition;
            $dyn.log.list && $dyn.log.list("List: recalculateRowIndex: Updating ChangeSelectedIndex row from %s to %s", initialRowIndex, newRowIndex);
            return newRowIndex;
        },

        forceRenderCardListIE: function () {
            /// This is a workaround for an IE rendering issue.
            /// IE renders cardlist grid in one column due to some IE only issues with nested flexbox container with flex-wrap on them
            /// With nested flexbox container sometimes IE calculates the height and width of top container incorrectly which causes the inner containers to all render in one column
            /// However, forcing the browser to re-render the parent container will fix the problem
            /// Here we check if the layout provider is css3grid (the provider we use for IE) and also if 'cardlist' class exists on grid
            /// and then hide and show the list body to force the browser to re-render (recalculate) flex wrap behavior
            if ($dyn.layout.provider.name === $dyn.layout.Providers.css3grid && $dyn.value(this.VisibleColumnCount) !== 1 && this.element.classList.contains('cardList')) {
                var display = this._listBodyElement.style.display;
                this._listBodyElement.style.display = 'none';
                this._listBodyElement.style.display = display;
            }
        },

        isRowItemFullyVisible: function (item) {
            var isVisible = true;
            var newScrollTop = 0;

            if (item && item.offsetParent) {
                // row's offsetParent is the listbody, whose offsetTop will include the loading more data div if visible
                var itemOffsetTop = item.offsetTop + item.offsetParent.offsetTop;
                if (itemOffsetTop < this.GetScrollRegionTop()) { // row is above the scroll region
                    isVisible = false;
                    newScrollTop = itemOffsetTop;
                } else {
                    // using offsetHeight because we want to include any padding/border
                    var bottomOfItem = itemOffsetTop + item.offsetHeight;
                    var bottomOfScrollRegion = this.GetScrollRegionTop() + this.GetScrollRegionHeight();
                    if (bottomOfItem > bottomOfScrollRegion) { // row is below the scroll region
                        isVisible = false;
                        // only want to scroll the distance needed to make row fully visible
                        newScrollTop = this.GetScrollRegionTop() + (bottomOfItem - bottomOfScrollRegion);
                    }
                }
            }

            return { visible: isVisible, scrollTop: newScrollTop };
        },

        /// <summary>
        /// Scroll the item into focus if a scroll container has been registered and the row item is not currently visible
        /// </summary>
        scrollItemIntoView: function (item) {
            if (item) {
                var answer = this.isRowItemFullyVisible(item);
                if (!answer.visible) {
                    this.SetScrollRegionTop(answer.scrollTop);
                }
            }
        },

        // Applys cache coordinate system shift that happens when cache expands upwards (0 index points to another record)
        // Updates server cache related indexes
        applyServerCacheCoordinateShift: function (shift) {
            this._rangePivot += shift;
            this.clientCacheStart += shift;

            if (this._serverCacheCoordinateShiftHandler) {
                this._serverCacheCoordinateShiftHandler(shift);
            }
        },        

        /**
         * @function CalculateNumberOfVisibleRows
         * @memberof $dyn.controls.List#
         * @internal
         * @summary Calculates number of visible rows in the viewport
         * @returns {Number} Number of visible rows
         */
        CalculateNumberOfVisibleRows: function () {
            var numVisibleItems = 0;
            var calculatedRowHeight = this.CalculateRowHeight();
            if (calculatedRowHeight > 0) {
                numVisibleItems = Math.floor(this.GetScrollRegionHeight() / calculatedRowHeight);
            }

            return numVisibleItems;
        },

        /**
         * @function FindActiveElement
         * @memberof $dyn.controls.List#
         * @protected
         * @summary Finds the active element within active row
         * @returns {Object} An active element if active row has one, null otherwise
         */
        FindActiveElement: function () {
            if (document.activeElement) {
                return this.GetSelectedRow().find(document.activeElement);
            }

            return null;
        },

        /**
         * @function CurrentPageIndex
         * @memberof $dyn.controls.List#
         * @protected
         * @summary Gets the current page index of the List
         * @returns {Integer} Current page index
         */
        CurrentPageIndex: function () {
            // TODO: Is this used on the Grid? (Remove?)
            var currentIndex = -1;
            var ds = $dyn.value(this.DataSource);
            if (ds.length > 0 && ds[0] && ds[0].parent) {
                currentIndex = $dyn.value(ds[0].parent.pageIndex);
            }
            return currentIndex;
        },
        
        /**
         * @function HasNextPage
         * @memberof $dyn.controls.List#
         * @protected
         * @summary Gets if the grid has next page.
         * @returns {boolean} returns true when grid has next page else returns false.
         */
        HasNextPage: function () {
            var hasNextPage = false;
            var ds = $dyn.value(this.DataSource);
            if (ds.length > 0 && ds[0] && ds[0].parent) {
                hasNextPage = $dyn.value(ds[0].parent.hasNextPage);
            }
            return hasNextPage;
        },

        /**
         * @function HasSinglePage
         * @memberof $dyn.controls.List#
         * @internal
         * @summary Indicates whether List has single page of rendered rows
         * @returns {Boolean} True if the List has amount of rows that exceed the page size, False otherwise
         */
        HasSinglePage: function () {
            var currentIndex = -1;
            var clientPageSize = -1;
            var ds = $dyn.value(this.DataSource);
            var numRows = $dyn.length(this.DataSource);
            if (ds && ds.length > 0 && ds[0] && ds[0].parent) {
                currentIndex = $dyn.value(ds[0].parent.pageIndex);
                clientPageSize = $dyn.value(ds[0].parent.clientPageSize);
            }
            /// Notice that the function has strange logic that prevents grid from having paging buttons (as it is invoked from ArePagingButtonsVisible)
            /// when data source doesn't support page index and page size properties (currentIndex === -1 or clientPageSize === -1)
            var pageSize = (clientPageSize && clientPageSize !== -1) ? clientPageSize : this.PageSize;
            if (numRows === 0 || (numRows < pageSize && currentIndex === 0) || clientPageSize <= 0) {
                return true;
            }
            return false;
        },

        /**
         * @function GetActiveCell
         * @memberof $dyn.controls.List#
         * @private
         * @summary Returns an active cell name
         * @returns {String} Active cell name
         */
        GetActiveCell: function () {
            return this._rowOperationsManager && this._rowOperationsManager.getActiveCell();
        },

        /**
         * @function IsNavigationAction
         * @memberof $dyn.controls.List#
         * @private
         * @summary Indicates whether or not the event should trigger a navigation action
         * @param {event} event - The jQueryEvent which may cause a navigation action
         * @returns {Boolean} True if a naviation action should be performed; otherwise false
         */
        IsNavigationAction: function (event) {
            return $dyn.value(this.NavigationEnabled) && (this.IsCardList() || this.TargetSupportsNavigation(event));
        },

        IsCardList: function () {
            if (this._isCardList === undefined) {
                this._isCardList = $(this.element).hasClass('cardList');
            }
            return this._isCardList;
        },

        IsInsidePreviewForm: function() {
            if (this.Root && this.Root.IsPreviewForm) {
                return true;
            }
            return false;
        },

        TargetSupportsNavigation: function (event) {
            var listCell = $(event.target).closest('.listCell');
            return listCell && listCell.hasClass('supportsNavigation');
        },

        /// <summary>
        /// Determines whether or not the secondary navigation action is valid.
        /// </summary>
        /// <param name="event">The event representing the secondary navigation action.</param>
        /// <returns><c>true</c> if the secondary navigation action is allowed; otherwise, <c>false</c>.</returns>
        IsValidSecondaryNavigation: function (event) {
            var closestListItem = $(event.target).closest('.listItem');
            return closestListItem.length === 1 || closestListItem.hasClass('marked');
        },

        /**
        * @function
        * @memberof $dyn.controls.List#
        * @private
        * @summary This method should only be used from Test Framework. Some instances of HierarchicalGridBase.js uses this but that should be removed.
        * @description FocusRow to a row index.
        * @param {rowIndex} rowIndex - rowIndex of target row
        * @param {markMode} markMode - markMode of target row
        * @param {inputMode} inputMode - inputMode of target row defining mouse or row key down
        * @param {event} event - event triggering the function
        * @returns {rowIndex} rowIndex - rowIndex of the target row
        */
        FocusRow: function (rowIndex, markMode, inputMode, event) {
            var isFastEdit = $dyn.peek(this._fastEdit);
            if (rowIndex !== $dyn.peek(this.SelectedIndex) || isFastEdit) {
                if (isFastEdit) {
                    var rowElement = this._renderer.getUserSelectedElement();
                    var targetRowId = this._renderer._rowIds[rowIndex];
                    if (rowElement) {
                        var currentRowId = this._renderer.getInternalRowIdByElement(rowElement);
                        var selectedRowIndex = this._renderer.getActivePageRelatedIndex(currentRowId);

                        //selectedRowIndex undefined is for pending rows
                        if (targetRowId != currentRowId || selectedRowIndex === undefined) {
                            this._markingAction = markMode;

                            if (selectedRowIndex === undefined) {
                                selectedRowIndex = 0;

                                //As this is used for relative position from current selected row, we have to make rowIndex as 1 when attempting to select first row
                                if (rowIndex == 0) {
                                    rowIndex = 1;
                                }
                                //As direction is not avaialable, we have to assume when called from test it should always attempt to go up from pending rows.
                                rowIndex = (-1) * rowIndex;
                            }

                            if (inputMode === $dyn.ui.InputType.mouse) {
                                this.FocusRowRelativeToActiveRow(null, markMode, inputMode, event);
                            }
                            else {
                                this.FocusRowRelativeToActiveRow(rowIndex - selectedRowIndex, markMode, inputMode, event);
                            }
                        }
                    }
                }
                else {
                    this._markingAction = markMode;
                    this.FocusRowRelativeToActiveRow(rowIndex - $dyn.value(this.SelectedIndex), markMode, inputMode, event);
                }
            }

            return rowIndex;
        },

        FocusRowRelativeToActiveRow: function (relativePosition, markMode, inputMode, event, restrictToRenderedRows) {
            return this._rowOperationsManager.FocusRowRelativeToActiveRow(relativePosition, markMode, inputMode, event, restrictToRenderedRows);
        },
        
        RenderedRowsCount: function () {
            return this._renderer.getRenderedRowCount();
        },
        
        _calculateMarkMode: function (event) {
            return this._rowOperationsManager.calculateMarkMode(event);
        },

        _executeRowMarkUpdate: function () {
            return this._rowOperationsManager.ExecuteRowMarkUpdate();
        },

        _performNonEditModeNavigationAction: function (event) {
            return this._rowOperationsManager.PerformNonEditModeNavigationAction(event);
        },
        
        _newRecord: function (targetRowElement) {
            return this._renderer.newRecord(targetRowElement);
        },

        _deleteRecord: function (targetRowElement, event) {
            return this._renderer.deleteRecord(targetRowElement, event);
        },

        _isLastRow: function () {
            var rowElement = this._renderer.getUserSelectedElement();
            var currentRowId = this._renderer.getInternalRowIdByElement(rowElement);
            return this._dataProvider.isBackendLastRow(currentRowId);
        },

        _isFirstRow: function () {
            return this.SelectedIndex() === 0;
        },

        _getListRowChangeShortCutDescription: function (listContext, keys) {
            var shortCutDescription = '';
            var shortCutKeyCode = keys.keyCode;
            if (shortCutKeyCode == $dyn.ui.KeyCodes.downArrow) {
                shortCutDescription = $dyn.label('Grid_NextRowShortcut');
                if (listContext._isLastRow()) {
                    if (listContext.CreateNewRecordOnKeyDown) {
                        shortCutDescription = $dyn.label('Grid_NextRowNewLineShortcut');
                    }
                } else if (keys.shift && !keys.ctrl) {
                    shortCutDescription = $dyn.label('Grid_NextRowToSelectedSetShortcut');
                } else if (!keys.shift && keys.ctrl) {
                    shortCutDescription = $dyn.label('Grid_NextRowButNotSelectShortcut');
                } 
            }
            else if (shortCutKeyCode == $dyn.ui.KeyCodes.upArrow) {
                shortCutDescription = $dyn.label('Grid_PreviousRowShortcut');
                if (keys.shift && !keys.ctrl) {
                    shortCutDescription = $dyn.label('Grid_PreviousRowToSelectedSetShortcut');
                } else if (!keys.shift && keys.ctrl) {
                    shortCutDescription = $dyn.label('Grid_PreviousRowButNotSelectShortcut');
                }
            } 
            return shortCutDescription;
        },

        /**
        * @function
        * @memberof $dyn.controls.List#
        * @private
        * @summary The method that returns if element is an active row element
        * @param {HTMLElement} controlElement
        * @returns {Boolean} True if element is an active row element
        */
        isControlInActiveRow: function (controlElement) {
            var isControlInActiveRow = false;
            var currentElement = controlElement;
            var rowid;
            if (currentElement) {
                do {
                    rowid = currentElement.getAttribute('data-dyn-rowid');
                    currentElement = currentElement.parentElement;
                } while (rowid == undefined && currentElement);
            }
            if (rowid && this._syncManager && this._syncManager.isActiveRow && this._syncManager.isActiveRow(parseInt(rowid))) {
                isControlInActiveRow = true;
            }
            return isControlInActiveRow;
        },

        headerScroll: function (event) {
            this.SyncHorizontalScrollFromHeader();
        },

        headerFocusOut: function (event) {
            var destinatonTargetElement = document.activeElement;
            if (event && event.relatedTarget) {
                destinatonTargetElement = event.relatedTarget;
            }
            if ($(destinatonTargetElement).closest('.listAsTable-HeaderRow, .columnHeader-popup').length <= 0) {
                this.AllowHeaderFocus(false);
                if (this._headerTabbingContainerDispose) {
                    this._headerTabbingContainerDispose();
                    this._headerTabbingContainerDispose = null;
                }
            }
        },

        ToggleMark: function () {
            // Default implementation of ToggleMark
            var markedIndexes = $dyn.value(this.MarkedIndexes);
            var selectedIndex = $dyn.value(this.SelectedIndex);
            var currentValue = markedIndexes[selectedIndex];
            markedIndexes[selectedIndex] = !currentValue;
            if ($dyn.isObservable(this.MarkedIndexes)) {
                this.MarkedIndexes.valueChanged.raise();
            }
        },

        hasMarkedRows: function () {
            return this._dataProvider.hasMarkedRows();
        },

        _getIsLoading: function () {
            return $dyn.value(this._syncManager._syncEngineState) === $dyn.util.SyncEngineState.active;
        },

        _onTabKey: function (evt) {
            // tab on the list body (or descedant)
            this._tabFocusing = true;
            $dyn.log.list && $dyn.log.list('List: [fast] Detecting user focus');

            var rowElement = this._renderer.getUserSelectedElement();
            if (rowElement) {
                var currentRowId = this._renderer.getInternalRowIdByElement(rowElement);
                this._rowOperationsManager.updateActiveElement(rowElement, true);
            }

            setTimeout(this._tabCallback.bind(this));
        },

        _tabCallback: function () {
            if (this._tabFocused) {
                $dyn.log.list && $dyn.log.list('List: [fast] Detecting user focus success');
            } else {
                if ($dyn.debug.general) {
                    var listElement = $(document.activeElement).closest('.listRoot')[0];

                    if (listElement !== this.element) {
                        $dyn.log.list && $dyn.log.list('List: [fast] Detecting user focus outside of grid');
                    } else {
                        $dyn.log.list && $dyn.log.list('List: [fast] Detecting user focus failed');
                    }
                }

                this._rowOperationsManager._activeCell("");
            }

            this._tabFocusing = false;
            this._tabFocused = false;
        },

        _focusIn: function (evt) {
            // focus in on the list body
            if (this._tabFocusing) {
                this._tabFocused = true;
            }

            var controlToSelect = this._getParentControlName(evt.target);
            this._rowOperationsManager._activeCell(controlToSelect);
            var targetRowElement = $(evt.target).closest('.listItem')[0];
            var targetRowId = this._renderer.getInternalRowIdByElement(targetRowElement);
            var currentDataSelectedRowId = $dyn.peek(this._dataProvider.DataSelectedRowId);
            if (targetRowId !== currentDataSelectedRowId) {
                // The grid handles focus events instead of other container controls such as the form)
                evt.stopImmediatePropagation();
            }
        },

        /**
        * @function
        * @memberof $dyn.controls.List.List#
        * @public
        * @summary The functionality is responsible for handling external focus change.
        * @param {element} element - An element to focus
        * @param {forceUpdateActiveCell} forceUpdateActiveCell - Boolean if we need to update active cell before focus
        * @returns {Boolean} False if Grid handled the focus, True otherwise
        */
        tryFocus: function (element, forceUpdateActiveCell) {
            if (!element && $dyn.value(this.IsNoResultsMessageDisplayed)) {
                $(this.getNoRowIndicator()).focus();
                $dyn.log.focus && $dyn.log.focus("Focus: List(List.Id: %s)->tryFocus Set focus to grid noRowIndicator", this.Id);
                return true;
            }
            else {
                return this._renderer.tryFocus(element, forceUpdateActiveCell);
            }
        },

        getFocusCallback: function (focusCallback) {
            return function () {
                focusCallback && focusCallback();
            };
        },

        _getVirtualControl: function (virtualizedElement) {
            var self = this;

            var controlElement = $(virtualizedElement).closest('[data-dyn-controlname]').addBack()[0];
            var dynContext = controlElement && controlElement.dynContext;
            if (controlElement && !dynContext) {
                var controlSelector = selector().attr('data-dyn-controlname', controlElement.getAttribute('data-dyn-controlname'));
                var liveElement = controlSelector.find(this._inactiveItemElement)[0];
                var liveControl = $dyn.context(liveElement);
                if (liveElement && liveControl) {

                    var control = Object.create(liveControl);

                    Object.defineProperties(control, {
                        index: {
                            get: function () {
                                var renderer = self._renderer;
                                if (!this.__listRowId) {
                                    var listItem = $(virtualizedElement).closest('.listItem')[0];
                                    this.__listRowId = renderer.getInternalRowIdByElement(listItem);
                                }
                                return self._renderer.getActivePageRelatedIndex(this.__listRowId);
                            },
                            enumerable: false,
                        },
                        serverIndex: {
                            get: function () {
                                return self.ConvertPageIndexToServerCacheIndex(this.index) + 1; // dbcacherownumber is 1 based
                            },
                            enumerable: false,
                        },
                        _Children: {
                            value: undefined,
                            writable: true,
                        },
                        Children: {
                            get: function () {
                                if (!this._Children) {
                                    var childControls = [];

                                    // Note: Would be nice to have the exact location (i.e. xpath like) of the children based on the template for the element
                                    var content = $('[data-dyn-content]', this.element);
                                    var children = content.children();
                                    for (var i = 0; i < children.length; i++) {
                                        var child = children[i];

                                        var childControlWrapper = self._getVirtualControl(child);
                                        if (childControlWrapper && childControlWrapper.current) {
                                            childControls.push(childControlWrapper.current);
                                        }
                                    }

                                    this._Children = $dyn.observable(childControls);
                                }
                                return this._Children;
                            }
                        }
                    });

                    controlElement.dynContext = control.dynContext = new $dyn.DataContext(control, liveElement.dynContext);
                    control.element = controlElement;
                    control.attached && control.attached();

                    return control.dynContext;
                }
            }

            return dynContext;
        },
        _supportsMarking: function () {
            var isMarkingAllowed = false;
            var isMarkingEnabled = $dyn.value(this.IsMarkingEnabled);
            var isSelectionIndicatorVisible = $dyn.value(this.IsSelectionIndicatorVisible);

            if (isMarkingEnabled && isSelectionIndicatorVisible) {
                isMarkingAllowed = true;
            } else if (isMarkingEnabled && !isSelectionIndicatorVisible) {
                // If isMarkingEnabled = False and isSelectionIndicatorVisible = Yes then marking is allowed when modifier keys are pressed.
                // Only when we do a single select marking is disabled.
                isMarkingAllowed = this._markingAction !== $dyn.controls.List.MarkMode.select;
            } else if (!isMarkingEnabled && isSelectionIndicatorVisible) {
                // If isSelectionIndicatorVisible = True and isMarkingEnabled = No then marking is allowed only on a single select and not when you
                // press modifier keys.
                isMarkingAllowed = this._markingAction == $dyn.controls.List.MarkMode.select ? true : false;
            }

            return isMarkingAllowed;
        },

        SetMarkAllRecordsMode: function (markState) {
            // Default implementation of SetMarkAllRecordsMode
            var rowCount = $dyn.length(this.DataSource);
            var markedIndexes = this._dataProvider.MarkedIndexes();
            for (var i = 0; i < rowCount; i++) {
                markedIndexes[i] = markState;
            }

            this._dataProvider.MarkedIndexes.valueChanged.raise();

            var allRows = this._getAllRowElements();
            for (var i = 0, len = allRows.length ; i < len; i++) {
                var rowElement = allRows[i];
                this._renderer.setRowMarking(rowElement, $dyn.value(markState));
            }

            $dyn.setValue(this, 'MarkAllRecords', markState);
        },

        ToggleMarkAllRecordsMode: function () {
            /// <summary>
            /// Toggles the mark all records mode.
            /// </summary>

            var self = this;
            if (self._supportsMarking()) {
                self._renderer.allRecordsMarkTriggered = true;
                // Note: This is operation is not sync'd by the sync manager
                $dyn.callFunction(self.SetMarkAllRecordsMode, self, !$dyn.value(self.MarkAllRecords));
            }
        },

        FindRowAtIndex: function (pageIndex) {
            var position = this.ConvertPageIndexToClientCacheIndex(parseInt(pageIndex));
            return this._renderer._getRenderedRowElementByIndex(position);
        },

        _getAllRowElements: function () {
            return $(this.element).find('.listBody .listItem');
        },

        /**
        @memberof $dyn.controls.List#
        @public
        @function UpdateClientCacheStart
        @summary updates client cache start
        @param {value} value - value of start position
        @param {noOfBufferRows} noOfBufferRows - value of buffers rows if loading buffer div is visible
        */
        UpdateClientCacheStart: function (value, noOfBufferRows) {
            var clientCacheStartShift = 0;
            if(this.clientCacheStart > 0){
                //This keeps track of the last position before cache start is updated. Required for fixing scrolling jump issue.
                this.clientLastStartPosition = this.clientCacheStart;
            }

            if (this.clientCacheStart !== undefined && this.clientCacheStart !== null && value !== undefined && value !== null) {
                clientCacheStartShift = this.clientCacheStart - value;
            }

            if (noOfBufferRows > 0) {
                this.clientCacheStart = value + noOfBufferRows;
            }
            else {
                this.clientCacheStart = value;
            }

            if (this._clientCacheStartUpdated) {
                this._clientCacheStartUpdated(clientCacheStartShift);
            }
        },
        ConvertPageIndexToServerCacheIndex: function (pageIndex) {
            return pageIndex + $dyn.peek(this.FetchedDataStartPosition);
        },

        ConvertPageIndexToClientCacheIndex: function (pageIndex) {
            return this.ConvertPageIndexToServerCacheIndex(pageIndex) - this.clientCacheStart;
        },

        ConvertClientCacheIndexToServerCacheIndex: function (clientCacheIndex) {
            return clientCacheIndex + this.clientCacheStart;
        },

        ConvertClientCacheIndexToPageIndex: function (clientCacheIndex) {
            return this.ConvertClientCacheIndexToServerCacheIndex(clientCacheIndex) - $dyn.peek(this.FetchedDataStartPosition);
        },

        ConvertServerCacheIndexToClientCacheIndex: function (serverCacheIndex) {
            return serverCacheIndex - this.clientCacheStart;
        },

        ConvertServerCacheIndexToPageIndex: function (serverCacheIndex) {
            return serverCacheIndex - $dyn.peek(this.FetchedDataStartPosition);
        },

        ChangeSelectedIndex: function (event) {
            var self = this;
            $dyn.ui.updateBlock(null, function () {
                // Default implementation of the command.  Can be overridden
                if (Array.isArray(event)) {
                    var rowIndex = event[0];
                    var markMode = event[1];
                    var rangePivot = event[2];
                    var callback = event[4]; //3rd paramater is shouldThrottle

                    var rowCount = self.DataSource ? $dyn.value(self.DataSource).length : 0;

                    // Update the MarkedIndexes array
                    var markedIndexes = self.MarkedIndexes();
                    switch (markMode) {
                        case $dyn.controls.List.MarkMode.select:
                            // Unmark all marked records
                            for (var i = 0; i < rowCount; i++) {
                                if (markedIndexes[i])
                                    markedIndexes[i] = false;
                            }
                            // Mark the record at rowIndex
                            markedIndexes[rowIndex] = true;
                            break;
                        case $dyn.controls.List.MarkMode.toggle:
                            markedIndexes[rowIndex] = !markedIndexes[rowIndex];
                            break;
                        case $dyn.controls.List.MarkMode.rangeSelect:
                            var startIndex, endIndex;
                            if (rangePivot > rowIndex) {
                                startIndex = rowIndex;
                                endIndex = rangePivot;
                            } else {
                                startIndex = rangePivot;
                                endIndex = rowIndex;
                            }
                            // Unmark all marked records
                            for (var i = 0; i < rowCount; i++) {
                                if (markedIndexes[i])
                                    markedIndexes[i] = false;
                            }
                            //Mark records in the range
                            for (var i = startIndex; i <= endIndex; i++) {
                                markedIndexes[i] = true;
                            }
                            break;
                        case $dyn.controls.List.MarkMode.clearMark:
                            // Unmark all marked records
                            for (var i = 0; i < rowCount; i++) {
                                if (markedIndexes[i])
                                    markedIndexes[i] = false;
                            }
                            break;
                    }

                    self.MarkedIndexes.valueChanged.raise();

                    // Update SelectedIndex property on the grid
                    self.SelectedIndex(rowIndex);
                    $dyn.async(function () {
                        callback && callback.call(self, true);
                    });
                }
            }, this);
        },

        // Fast Edit review
        setChildControlFocus: function ($element) {
            ///<sumamry>
            /// Sets focus to the first focusable control in the container.
            ///</sumamry>

            var self = this;
            var element = $element || self.element;

            // Find the first focusable child control
            var childElement = $dyn.util.findFirstFocusableControl(element);

            // Call setFocus with the parent form for this container as the root.
            if (childElement) {
                // Return the control that was found to focus, if there is one.
                this.setFocus(childElement, self.Root);
                return childElement;
            }
        },

        /**
        @memberof $dyn.controls.List#
        @public
        @function findFieldByNameInHeader
        @summary gets field in column header
        @param {controlName} controlName - name of the control
        @returns {Object} Object - column header
        */
        findFieldByNameInHeader: function (controlName) {
            return $.find("[data-dyn-columnname='" + controlName + "'] > .columnHeaderWrapper", this._headerRowElement);
        },

        /**
        @memberof $dyn.controls.List#
        @public
        @function findFieldByName
        @summary gets field in user selected element
        @param {controlName} controlName - name of the control
        @returns {Object} Object - user selected control
        */
        findFieldByName: function (controlName) {
            if (this.AllowHeaderFocus()) {
                return this.findFieldByNameInHeader(controlName);
            } else {
                var userSelectedElement = this._renderer.getUserSelectedElement();
                if (userSelectedElement) {
                    // Initially check whether active row has the given name
                    if ($(userSelectedElement).attr('data-dyn-controlname') === controlName) {
                        return $(userSelectedElement);
                    }
                    return $.find("[data-dyn-controlname='" + controlName + "']", userSelectedElement);
                }
            }
        },

        /**
        @memberof $dyn.controls.List#
        @public
        @function findFieldByNameInServerSelectedRow
        @summary gets field in server selected elment
        @param {controlName} controlName - name of the control
        @returns {Object} Object - server selected control
        */
        findFieldByNameInServerSelectedRow: function (controlName) {
            if (this.AllowHeaderFocus()) {
                return this.findFieldByNameInHeader(controlName);
            } else {
                //This gives the row element
                var serverSelectedElement = this._renderer.getServerSelectedElement();
                if (serverSelectedElement) {
                    //Finding field in row element
                    return $.find("[data-dyn-controlname='" + controlName + "']", serverSelectedElement);
                }
            }
        },

        getActiveRowClientIndex: function () {
            var userSelectedRenderedRow = this._renderer.getUserSelectedRenderedRow();
            if (userSelectedRenderedRow) {
                return userSelectedRenderedRow.order;
            }
        },

        AreElementsCrossBoundary: function (element, anotherElement) {
            var self = this;
            var getCellContext = function (cellElement) {
                var cellElementContext = $dyn.context(cellElement);
                if (!cellElementContext) {
                    var elementControlName = self._getParentControlName(cellElement);
                    if (elementControlName) {
                        var selectedRowCellElement = $(self._selectedRow).find('[data-dyn-controlname="' + elementControlName + '"]')[0];
                        if (selectedRowCellElement) {
                            cellElementContext = $dyn.context(selectedRowCellElement);
                        }
                    }
                }
                return cellElementContext;
            };

            var elementContext = getCellContext(element);
            var anotherElementContext = getCellContext(anotherElement);

            if (elementContext && anotherElementContext) {
                return $dyn.peek(elementContext.ServiceBoundary) !== $dyn.peek(anotherElementContext.ServiceBoundary);
            }
        },

        _layoutCompleted: function () {
            this.postRenderInternal(true);
            this.forceRenderCardListIE();
            $(this.element).trigger($dyn.forms.Events.gridLayoutCompleted);
        },

        _getHeaderId: function (headerData) {
            return headerData && $dyn.value(headerData.Name);
        },

        _getHeaderVersion: function (headerData) {
            return headerData && headerData.Version;
        },

        _headerPrefixTemplate: 'ListMarkAllHeader',

        _getHeaderPrefix: function () {
            if (!this._headerPrefix) {
                this._headerPrefix = $dyn._getTemplate(this._headerPrefixTemplate).cloneNode(true);
                this._headerPrefix.removeAttribute('id');
                $dyn.dataBind(this._headerPrefix, this.element.dynContext);
            }

            return this._headerPrefix;
        },

        _headerScrollDivTemplate: 'HeaderScrollDivPad',
        _getHeaderScrollSuffix: function () {
            if (!this._headerScrollSuffix) {
                this._headerScrollSuffix = $dyn._getTemplate(this._headerScrollDivTemplate).cloneNode(true);
                this._headerScrollSuffix.removeAttribute('id');
                $dyn.dataBind(this._headerScrollSuffix, this.element.dynContext);
            }

            return this._headerScrollSuffix;
        },

        _renderHeader: function () {
            this.RenderedHeaderMetadata(this.ColumnHeaderMetadata());
        },

        _headerPreRender: function () {
            var prefixElement = this._getHeaderPrefix();
            var parent = prefixElement && prefixElement.parentNode;
            if (parent) {
                parent.removeChild(prefixElement);
            }
        },

        _headerPostRender: function () {
            var headerElement = this._headerRowElement;
            if (headerElement) {
                var prefixElement = this._getHeaderPrefix();
                if (prefixElement) {
                    headerElement.insertBefore(prefixElement, headerElement.firstChild);
                }

                if ($dyn.shell.isEdge) {
                    var self = this;
                    if (!this._noResultsHeaderObserver) {
                        // We only show vertical scroll bar if there are records
                        this._noResultsHeaderObserver = this.usingDisposable($dyn.observe(this.IsNoResultsMessageDisplayed, function (isShown) {
                            var headerSuffixElement = self._getHeaderScrollSuffix();
                            if (headerSuffixElement) {
                                if (isShown) {
                                    headerSuffixElement.style.width = "0px";
                                } else {
                                    // calculate scrollbar width
                                    var scrollRegion = self.GetScrollRegion();
                                    if (scrollRegion) {
                                        var scrollSize = scrollRegion.offsetWidth - scrollRegion.clientWidth;
                                        headerSuffixElement.style.width = scrollSize + "px";
                                    }
                                }
                            }
                        }));
                    }

                    var suffixElement = this._getHeaderScrollSuffix();
                    if (suffixElement) {
                        headerElement.appendChild(suffixElement, headerElement.lastChild);
                    }
                }
            }
            var colCount = $dyn.length(this.RenderedHeaderMetadata);
            if ($dyn.value(this.IsMarkingEnabled)) {
                colCount++;
            }

            this.element.setAttribute('aria-colcount', colCount);
        },

        _headerIterationCallback: function (element, index) {
            var columnHeader = $dyn.context(element.querySelector(".columnHeaderWrapper"));
            if (columnHeader && columnHeader.Index) {
                columnHeader.Index(index + 1);
            }
        },

        _resizeControlColumn: function (controlIndex, width, updatePersonalizationSettings) {
            // Resizes a control based column inside the grid
            var self = this;

            // Map to the appropriate contained control and set its width so that the update happens synchronously.
            // Setting the width property on the child control should cause all instances throughout the foreach to be updated.
            var childControlContexts = self.getDataColumnContextsByIndex(controlIndex, true);
            if (childControlContexts.length) {
                var activeElementFound = false;
                for (var i = 0; i < childControlContexts.length; i++) {
                    if (childControlContexts[i]) {
                        var controlElements = self.findControlElementsInAllRows(childControlContexts[i].Name);
                        var j, len;
                        for (j = 0, len = controlElements.length; j < len; j++) {
                            var childControl = $dyn.context(controlElements[j]);
                            if (!activeElementFound && childControl && childControl.LayoutWidth && (self.isControlInActiveRow(controlElements[j]) || self._dataProvider.Collection().length == 0)) {
                                childControl.LayoutWidth(width);
                                activeElementFound = true;
                            } else {
                                $dyn.bindingHandlers.sizing.widthChanged(controlElements[j], width);
                            }
                        }
                    }
                }

                if (updatePersonalizationSettings && childControlContexts.length === 1) {
                    // Persist personalization settings on the server.
                    $dyn.callFunction(childControlContexts[0].PersistPersonalization, childControlContexts[0], ["width", width]);
                }
            }
        },

        _processColumnHeaderResize: function (event, updatePersonalizationSettings, columnHeaderWidth) {
            var self = this;
            var target = $(event.target);
            var parentHeader = target.closest(".columnHeader");
            if (parentHeader.length === 1) {
                var closestWrapper = target.closest('.columnHeaderWrapper');

                // Attempt to line up control columns by index.
                var controlndex = parentHeader.parent().children(".columnHeader").index(parentHeader);
                closestWrapper.outerWidth(columnHeaderWidth);
                //Get the actual width of header after desired width is applied and then apply that width to control columns. Sometimes header can not achieve the desired width because of minimum width of the elements inside the header.
                var actualHeaderColumnWidth = $(closestWrapper).width();
                // Resize the control column thoroughly
                self._resizeControlColumn(controlndex, actualHeaderColumnWidth, updatePersonalizationSettings);
            }
        },

        _isInvalidMarkTarget: function (event) {
            /// <summary>
            /// Right clicking and ctrl-clicking JumpRef and HyperLink elements in a grid should not trigger a mark event.
            /// </summary>
            /// <param name="event">The event representing the click action.</param>
            /// <returns><c>true</c> if the target is not valid for initiating a record mark; otherwise, <c>false</c>.</returns>
            var self = this;
            var isInvalid = false;
            if (event) {
                var eventContext = event.target ? $dyn.context(event.target) : null;
                var isHyperlink = eventContext ? $dyn.value(eventContext.IsHyperlink) : false;
                var isNavigationAction = self.IsNavigationAction(event);
                if (isHyperlink || isNavigationAction) {
                    isInvalid = $dyn.util.clickedText(event.target);
                }
            }
            return isInvalid;
        },

        getTaskGuideParams: function (options) {
            options = $dyn.ui.Control.prototype.getTaskGuideParams.apply(this, [options]);
            var commandName = options.action;
            var stepArgs = options.arguments;
            var pagingButtons, rowIndex, rowElement;
            if (commandName.indexOf("LoadNextPage") !== -1) {
                var pagingButtons = $(this.element).children('.pagingButtons');
                if (pagingButtons) {
                    var nextButton = pagingButtons.children('[title="' + $dyn.label('Grid_Next') + '"]');
                    options.target = nextButton;
                    options.my = 'top center';
                    options.at = 'bottom center';
                }
            } else if (commandName.indexOf("LoadPreviousPage") !== -1) {
                pagingButtons = $(this.element).children('.pagingButtons');
                if (pagingButtons) {
                    var nextButton = pagingButtons.children('[title="' + $dyn.label('Grid_Previous') + '"]');
                    options.target = nextButton;
                    options.my = 'top center';
                    options.at = 'bottom center';
                }
            } else if (commandName.match('MarkActiveRow|UnmarkActiveRow|ChangeSelectedIndex|ChangeSelectedIndexInCache|NavigationAction')) {
                options.target = $(this.element);
            }
            return options;
        },

        SelectCell: function (event) {
            var controlName = event[1];
            var index = event[0];
            if (!this.Root || !this.Root.ActiveControl) {
                return;
            }
            if ($dyn.isObservable(this.Root.ActiveControl)) {
                this.Root.ActiveControl(controlName);
            } else {
                this.Root.ActiveControl = controlName;
            }
        },

        TransferInputFocus: function (rowElement) {
            this._renderer.TransferInputFocus(rowElement);
        },

        _getParentControlName: function (element) {
        	var parent = element;
        	while (parent && typeof(parent.getAttribute) === "function") {
        		var ctrlName = parent.getAttribute('data-dyn-controlname');
        		if (!ctrlName && $(parent).hasClass('checkMarkTarget') && parent.getAttribute('role') === 'checkbox') {
        			return 'checkMarkTarget';
        		}
        		var role = parent.getAttribute('data-dyn-role');
        		if (!ctrlName && parent.classList.contains('columnHeader')) {
        		    ctrlName = parent.getAttribute('data-dyn-columnname');
        		    if (ctrlName) {
        		        return ctrlName;
        		    }
        		}
        		if (ctrlName && (role && role !== 'RowTemplate')) {
        			return ctrlName;
        		} else {
        			parent = parent.parentNode;
        		}
        	}
        },

        Create: function () {
        },

        Delete: function () {
        },

        getColumnLabel: function (label, name, showLabel) {
            // Can be overriden to get a custom label.

            if ((showLabel == undefined) || $dyn.value(showLabel)) {
                return label;
            }

            return "";
        },

        getFilterability: function (isFilterable, fieldName, DataSource) {
            return $dyn.value(isFilterable) || $dyn.value(fieldName) == $dyn.util.dataAreaId
                && DataSource && $dyn.peek(DataSource)[0] && $dyn.peek(DataSource)[0].parent.IsCrossCompany;
        },

        columnMutator: function (element, index) {
            /// <summary>
            /// Mutates the content as appropriate for Tabular List.
            /// </summary>
            element.setAttribute("role", "gridcell");

            // Used data attribute "data-colindex" instead of aria-colindex, since screen readers incorrectly read aria-columnindex with some browsers.
            // Accessible aria-columnindex value will be generated automatically by browser.
            element.setAttribute("data-colindex", index + 1);
            element.classList.add('listCell');

            if (index === 0) {
                element.classList.add('listCell-firstCell');
            }
        },

        IsMarked: function (index) {
            return this.MarkedIndexes()[index];
        },

        rangePivotWithinPage: function () {
            return this._rangePivot - $dyn.value(this.FetchedDataStartPosition);
        },

        /// <summary>
        /// Notifies of any changes to the child hierarchy structure for the given target element.
        /// </summary>
        /// <returns>Returns a computed observable</returns>
        _rowElementGenerator: function (parentElement) {
            // This is used so that the computed value changes every time, to avoid
            // caching inside $dyn.computed.

            var self = this;
            return $dyn.computed(function () {

                var cellType = $dyn.value(self._fastEdit) && $dyn.value(self.AllowEdit) ? 'cellEditor' : 'cellRoleName';
                var recurseThroughChildHierarchy = function (element) {
                    var clonedElement = document.createElement(element.nodeName);

                    var controlName = element.getAttribute('data-dyn-controlname');
                    if (controlName) {
                        clonedElement.setAttribute('data-dyn-controlname', controlName);
                    }

                    var roleName = element.getAttribute('data-dyn-role');
                    if (roleName) {
                        var controlPrototype = $dyn.controls[roleName] && $dyn.controls[roleName].prototype;
                        roleName = controlPrototype ? (controlPrototype[cellType] || controlPrototype.cellRoleName || roleName) : roleName;
                        clonedElement.setAttribute('data-dyn-role', roleName);
                    }

                    var attributes = element.getAttribute('data-dyn-options');
                    if (attributes) {
                        clonedElement.setAttribute('data-dyn-bind', attributes.slice(1, -1));
                    }

                    // Observe the children and recurse.
                    var children = $dyn.value(element.dynChildren);
                    if (children) {
                        for (var i = 0, len = children.length; i < len; i++) {
                            var childElement = children[i];
                            var clonedChildElement = recurseThroughChildHierarchy(childElement);
                            clonedElement.appendChild(clonedChildElement);
                        }
                    }

                    return clonedElement;
                };

                var copy = recurseThroughChildHierarchy(parentElement);
                return copy.children;
            });
        },

        _cachedInactiveItemElement: undefined,

        get _inactiveItemElement() {
            var self = this;
            var inactiveItemElement = this._cachedInactiveItemElement;
            if (!inactiveItemElement) {
                $dyn.log.list && $dyn.log.list('List: Generating inactive row element');
                inactiveItemElement = this._cachedInactiveItemElement = self.ChildHierarchy()[0];
            }

            if (inactiveItemElement && !$dyn.context(inactiveItemElement)) {
                if (!this._itemDataContext) {
                    this._listBodyRole = $dyn.role(this._listBodyElement);
                    this._itemDataContext = new $dyn.DataContext(self._inactiveItem, self._listBodyElement.dynContext);
                }
                $dyn.log.list && $dyn.log.list('List: Databinding inactive row element');
                this._listBodyRole.dataBindItem(inactiveItemElement, this._itemDataContext);
                $dyn.layout.arrange(inactiveItemElement);
            }

            return inactiveItemElement;
        },

        _resetCachedInactiveItemElement: function (newInactiveItemElement) {
            if (this._cachedInactiveItemElement) {
                $dyn.log.list && $dyn.log.list('List: Unbind inactive row');
                $dyn.unbind(this._cachedInactiveItemElement);
            }
            this._cachedInactiveItemElement = newInactiveItemElement ? newInactiveItemElement : null;
        },

        updateSchema: function (changes) {
            $dyn.log.list && $dyn.log.list('List: Inactive row schema changed (updateSchema)');

            this._resetCachedInactiveItemElement();
            this._rerenderOptions($dyn.peek(this._rerenderOptions) + 1);
        },

        rowClick: function (event) {
            // Can be called statically

            var list = $dyn.context($(event.currentTarget).closest('.listRoot')[0]);
            list.RowClicked(event);
        },

        _getValues: function () {
            // Internal use only

            var rows = $('.listBody', this.element).find('.listItem');
            var rowsArray = [];
            rows.each(function (index, element) {
                var row = $(element);
                var rowArray = new Array();
                row.find('.listCell:not(.repeater-template)').each(function (listCellIndex, listCell) {
                    var value = '';
                    if ($(listCell).is('.checkBox-container')) {
                        value = $(listCell).find('.checkBox').attr('aria-checked') === "true";
                    } else {
                        var foundInputs = $(listCell).find('input');

                        if (foundInputs.length > 0) {
                            // Simple taking the first input element won't cover all cases, but it is acceptable
                            // given the purposes of this method.
                            var inputElement = foundInputs[0];
                            value = inputElement.value;
                        }
                    }

                    rowArray.push(value);
                });
                rowsArray.push(rowArray);
            });

            return rowsArray;
        },

        _hasTextSelection: function (event) {
            // Internet Explorer selects text in the input field by itself when Ctrl/Shift key is pressed
            // We don't treat such a selection as text selected by user
            // As a result any selection with Ctrl/Shift pressed is not considered to be user selection
            if (event.ctrlKey || event.metaKey || event.shiftKey) {
                return false;
            }

            // Detect if there is a selection.  The browser will normally remove the selection as part of a click but if there is a selection that can indicate
            // that the user was not doing a click but actually was selecting text.
            var selectedText = window.getSelection().toString();
            if (selectedText) {
                $dyn.log.list && $dyn.log.list('List: row click canceled due to selection: %s', selectedText);
                return true;
            }

            var documentSelection = document.activeElement && $dyn.ui.selectionStart(document.activeElement) !== $dyn.ui.selectionEnd(document.activeElement);

            if (documentSelection) {
                $dyn.log.list && $dyn.log.list('List: row click canceled due to selection: start="%s", end="%s"',
                    $dyn.ui.selectionStart(document.activeElement),
                    $dyn.ui.selectionEnd(document.activeElement));

                return true;
            }

            return false;
        },

        ExecuteExportToExcel: function (onlyMarkedRows) {
            /// <summary>
            /// Exports the contents of this grid to Excel.
            /// </summary>
            /// <param name="onlyMarkedRows">A Boolean value indicating whether or not to limit the export to marked rows.</param>
            $dyn.callFunction(this.ExportToExcel, this, onlyMarkedRows);
        },

        GetHeightForHandler: function() {
            this._skipOffsetParentCheck = true;
            var height = this.GetScrollRegionHeight();
            this._skipOffsetParentCheck = false;

            return height;
        },

        GetScrollRegion: function () {
            if (!this._scrollRegion) {
                this._scrollRegion = this.element.getElementsByClassName("scrollRegion")[0];
                if (this.WindowResizeHandler) {
                    $(window).on("resize", this.WindowResizeHandler);
                }

            }
            return this._scrollRegion;
        },
        GetScrollRegionHeight: function () {
            //If an element is not displayed then its offsetParent is null
            if (this.GetScrollRegion() && (this._skipOffsetParentCheck || this._scrollRegion.offsetParent !== null)) {
                if (this.CachedScrollRegionHeight) {
                    return this.CachedScrollRegionHeight;
                } else {
                    this.UpdateScrollRegionHeight();
                    return this.CachedScrollRegionHeight;
                }
            } else {
                return 0;
            }
        },
        SetScrollRegionHeight: function (value) {
            this.GetScrollRegion().style.height = value + "px";
            this.UpdateScrollRegionHeight();
        },
        UpdateScrollRegionHeight: function () {
            // Want to use client height because we don't want to include space taken up by horizontal scrollbar
            this.CachedScrollRegionHeight = this.GetScrollRegion().clientHeight;
        },
        GetScrollRegionWidth: function () {
            if (this.CachedScrollRegionWidth) {
                return this.CachedScrollRegionWidth;
            }
            this.UpdateScrollRegionWidth();
            return this.CachedScrollRegionWidth;
        },
        UpdateScrollRegionWidth: function () {
            // OffsetWidth includes any border or vertical scrollbar
            this.CachedScrollRegionWidth = this.GetScrollRegion().offsetWidth;
        },
        GetScrollRegionTop: function () {
            if (this.CachedScrollRegionTop !== undefined && this.CachedScrollRegionTop !== null) {
                return this.CachedScrollRegionTop;
            }
            this.CachedScrollRegionTop = this.GetScrollRegion().scrollTop;
            return this.CachedScrollRegionTop;
        },
        UpdateScrollTop: function (value) {
            if (value < 0) {
                value = 0;
            }

            var oldValue = this.CachedScrollRegionTop;
            if (value !== undefined && value !== null) {
                this.CachedScrollRegionTop = value;
            } else {
                this.CachedScrollRegionTop = this.GetScrollRegion().scrollTop;
            }

            return oldValue !== this.CachedScrollRegionTop;
        },
        SetScrollRegionTop: function (value) {
            // Perform in microTask to prevent layout thrashing
            $dyn.microTask(function () {
                this.GetScrollRegion().scrollTop = value;
            }, this);
            
            this.UpdateScrollTop(value);
        },

        _updateDefaultActionEnabled: function () {
            var defaultActionEnabled = $dyn.value(this.NavigationEnabled);
            //Bug 243246: this should just pply the defaultActionEnabled property from inactive row, should not iterate over grid columns every time
            this._updateRowDefaultAction(this._cachedInactiveItemElement, defaultActionEnabled, '_lastInactiveDefaultControl');
            this._updateRowDefaultAction(this._selectedRow, defaultActionEnabled, '_lastActiveDefaultControl');
        },

        _updateRowDefaultAction: function (rowElement, defaultActionEnabled, lastDefaultControlField) {
            // If the default action is enabled on grid then the first column on grid that can have the default action will be the default action (DefaultActionEnabled).
            var lastDefaultControl = this[lastDefaultControlField];

            if (defaultActionEnabled && rowElement) {
                var defaultActionControl = this._getDefaultControl(rowElement);
                if (defaultActionControl) {
                    defaultActionControl.DefaultActionEnabled = true;
                }
                this[lastDefaultControlField] = defaultActionControl;
            }

            if (lastDefaultControl && (!defaultActionEnabled || lastDefaultControl !== defaultActionControl)) {
                lastDefaultControl.DefaultActionEnabled = false;
            }
        },

        /**
         * @function
         * @summary Responsible for sending default action control of grid
         * @private
         * @memberof $dyn.controls.List
         * @param {Object} rowElement - row element
         * @returns {Object} Control which has default action
         * @description
         * FLOW FOR GETTING DEFAULT CONTROL
         * 1) If the cached value is empty then get the first control which supports the defaults action
         * 2) Server is supposed to send control which has IsDefaultActionGridControl true when Default Action flag of SysClientPerf dialog is turned on
         * 3) If server sends that then we just need to get that control by looping all grid controls and once we get that we break the loop and return that control
         * 4) If server does not sends then the first control which is found in step 1 is cached and returned
         */
        _getDefaultControl: function (rowElement) {
            var columns = $dyn.children(rowElement);
            //After Bug 243246 is resolved _defaultActionControl should be cached.
            var _defaultActionControl;
            if (columns) {
                for (var i = 0, len = columns.length; i < len; i++) {
                    var column = columns[i];
                    var control = $dyn.context(column);
                    if (control && control.SupportsDefaultAction) {
                        //Grab the first control which supports default action so that we can use that if server does not sends it
                        if (!_defaultActionControl) {
                            _defaultActionControl = control;
                        }
                        //If server sends the default column then it takes the precedence and we do not need to look further
                        if ($dyn.value(control.IsDefaultActionGridControl)) {
                            _defaultActionControl = control;
                            break;
                        } 
                    }
                }
            }
            return _defaultActionControl;
        },

        _fastEditingAllowed: function () {
            return $dyn.value(this.AllowEdit) && $dyn.value(this._fastEdit);
        },

        hasPendingEdits: function () {
            return this._syncManager.hasPendingEdits();
        },

        _reevaluateSurroundingBlockingDiv: function () {
            if ($dyn.peek(this.fastEditingAllowed) && !$dyn.peek(this._syncManager.isQueueEmpty) && this._surroundingBlockingDivShown) {
                this._reevaluateElements = true;
                this.SurroundingBlockingDiv.removeClass("fadeInQuick");
                this.SurroundingBlockingDiv.show(this.allowedElements);
            }
        },

        dispose: function () {
            $dyn.ui.Control.prototype.dispose.call(this);
            $(document).off("densitychanged", this._densityChangeHandler);
            if (this.WindowResizeHandler) {
                $(window).off("resize", this.WindowResizeHandler);
            }

            if (this._cachedInactiveItemElement) {
                $dyn.unbind(this._cachedInactiveItemElement);
                this._cachedInactiveItemElement = null;
            }

            this._dataProvider && this._dataProvider.dispose && this._dataProvider.dispose();
            this._renderer && this._renderer.dispose && this._renderer.dispose();
            this._syncManager && this._syncManager.dispose && this._syncManager.dispose();
            this._editManager && this._editManager.dispose && this._editManager.dispose();
            this._rowOperationsManager && this._rowOperationsManager.dispose && this._rowOperationsManager.dispose();
            this._syncQueueEmptySubscription && this._syncQueueEmptySubscription.dispose && this._syncQueueEmptySubscription.dispose();
            this._msgBarObserver && this._msgBarObserver.dispose && this._msgBarObserver.dispose();
            this._surroundingBlockingDiv && this._surroundingBlockingDiv.destroy();
            this._tabbingContainerDispose && this._tabbingContainerDispose();
            this._headerTabbingContainerDispose && this._headerTabbingContainerDispose();
            this._reevaluateTimeout && this._reevaluateTimeout.cancel();
            this._reevaluateTimeout = undefined;
            this._syncQueueEmptySubscription = null;
            this._msgBarObserver = null;
            this._lastInactiveDefaultControl = null;
            this._lastActiveDefaultControl = null;
            this._scrollRegion = null;
        },
    });

    $dyn.controls.RowTemplate = function (data, element, parent) {
        $dyn.ui.Control.apply(this, arguments);
        this.ParentGrid = parent;
        this.ServerFocusableControl = parent;
    };

    $dyn.controls.RowTemplate.prototype = $dyn.extendPrototype($dyn.ui.Control.prototype, {
        ParentGrid: undefined,
        AllowPersonalization: false,

        focusCallback: function (options) {
            var self = this;
            var controlElement = options[0] ? options[0] : self.element;
            if (controlElement) {

                // Focus on the grid control.
                var elementToFocus = self.ParentGrid ? self.ParentGrid.element : $(controlElement).parents('[data-dyn-role="List"]')[0];
                if (elementToFocus) {
                    $dyn.util.focusElement(elementToFocus);
                }
                $dyn.log.focus && $dyn.log.focus('Focus: Grid->focusCallback set to: %s', self.Name);
            }
        }
    });

    $dyn.util.listEmpty = function (element, unbind) {
        if (unbind === undefined) {
            unbind = true;
        }
        var child;
        if (element) {
            while (child !== element.lastChild) {
                child = element.lastChild;
                if (child) {
                    element.removeChild(child);
                }
            }
        }
    };

    $dyn.bindingHandlers.list = function (targetElement, role) {
        var itemTask = $dyn.bindingHandlers.foreach.itemTask;
        var target = $(targetElement);
        if (!role) {
            role = $dyn.role(targetElement);
        }
        var listContext = $dyn.context(targetElement);
        var appliedBinding = false;
        // The template to use for the binding may have been cached before
        var selectedItemElement = targetElement.dynSelectedRow;
        if (!selectedItemElement) {
            // Current children are used as template
            var templateContainer = target.children().not(".repeater-template");

            templateContainer.attr($dyn.const.stopBindingAttribute, true);

            $dyn.util.listEmpty(targetElement);

            selectedItemElement = listContext._selectedRow = targetElement.dynSelectedRow = templateContainer[0].children[0];
        }

        /// INFINITE SCROLL LOGIC:
        /// STATEMENTS:
        /// - Rows in the grid are rendered based on 'collection' items provided from outside to the grid as DataSource
        /// - Collection is representing a projection of the cache
        /// - Grid has knowledge about cache based on fetchedDataStartPosition, CacheSize, dataBelow, dataAbove parameters
        /// - fetchedDataStartPosition - represents 0-based position in the cache that is the start of the collection
        /// - Cache can grow in both directions
        /// - dataAbove, dataBelow are representing whether cache can grow Up or Down from the current state
        /// - Grid renders row elements based on collection data and whitespaces: above and below row elements
        /// - Upper whitespace represents how many records are currently in the cache above first record in the collection
        /// - Lower whitespace represents how many records are currently in the cache below last record in the collection
        /// - Sizes of whitespaces are calculated based on rowHeight that is static by assumption
        ///
        /// SCROLLING FLOW:
        ///   SyncVerticalScrollOffset function is invoked on every scroll event -> It invokes fetchDataOnScroll function
        ///   if scroll position is outside of data triggers segment -> fetchDataOnScroll calculates new fetchDataStartPosition and fetchSize
        ///   and invokes LoadData function that under ServerForm scenario goes to server as command. ->
        ///   Right after LoadData call readjustScrollParametersByFetchCondition gets invoked with new fetchStart and fetchSize parameters. ->
        ///   This function updates dataTriggers so that no new immediate fetchDataOnScroll calls happens. -> Once new data comes to grid,
        ///   After rendering process adjustScrollParameters is invoked that calculates all scroll parameters including dataTriggers.
        ///   It also resizes White Spaces and applies coordinate system shift that can happen when cache grown up. ->
        ///   ensureFullViewPortContent is invoked that can also invoke LoadData if it decides that it is not enough data to fill the view and have good reserve
        ///
        /// SCROLL PARAMETERS:
        /// - cacheSize, fetchedDataStartPosition, dataAbove, dataBelow - parameters that are given to grid from outside
        /// - paddingRowsAbove/Below - representing the number of rows that can be rendereding the upper and lower whitespaces
        /// paddingRowsAbove can be more than fetchedDataStartPosition if dataAbove is true. We dedicate some space for the rows that are not in the cache,
        /// so that user can scroll there and request new rows by expanding the cache. Same for paddingRowsBelow
        /// - dataTriggerMax, dataTriggerMin - trigger points for scroll position of the scroll region.
        /// When scroll position goes out of these boundaries, we request new data
        /// - rowHeight - the height of the rendered row in pixels. rowCount - count of the rendered rows
        ///
        /// FUNCTIONS:
        /// - adjustScrollParameters - adjusts scroll parameters based on row height, rendered row count and collection data
        /// Resizes whitespaces.
        /// - readjustScrollParametersByFetchCondition - updates scroll parameters, based on requested fetch position and fetch size.
        /// Used after requesting the new data, updates data triggers, so that between request and rendering no redundant requests are going
        /// - applyPendingScrollShifts. Changes scroll position based on shifts of the cache that are pending.
        /// 'Shift' means that 0-position of the cache changed. Happens when we expand cache above.
        /// - ensureFullViewPortContent - Triggers the logic that makes sure that current visible area has data
        /// - fetchDataOnScroll - Requests data load if needed, based on data triggers.
        /// Takes into account the scroll direction and requests more data towards scroll direction
        /// Requests page size increase if current row count is not enough to fill visible space and non-visible reserve
        /// - syncVerticalScrollOffset - the handler of the scroll event that triggers infinite scroll logic if scroll position is outside of data triggers
        /// Check focus triggers to decide whether it is needed to restore focus
        /// - restoreSelectionFocus - invokes focus restoration logic on the Grid control if selected item is in the view
        /// Otherwise calculates focus triggers that indicate under which scroll position selected item goes into view.

        // FetchedDataStartPosition - Server cache co-ord, page start in db cache
        // SelectedIndex - Page coord - selected index in page

        // We will need the scroll region a lot, let's have it available
        var scrollRegion = $(listContext.element).find(".scrollRegion").first();

        // Ensure padding elements above and below the item collection
        // These padding elements are used for infinite scrolling
        var listContent = targetElement.parentNode;
        var upperWhiteSpaceElement = $(".listRoot-loadingAbove", listContent)[0];
        if (!upperWhiteSpaceElement) {
            upperWhiteSpaceElement = $("<div class='listRoot-loading listRoot-loadingAbove' style='display: none;'><span>" + $dyn.label('List_LoadingMoreData') + "</span></div>")[0];
            listContent.insertBefore(upperWhiteSpaceElement, targetElement);
        }
        var lowerWhiteSpaceElement = $(".listRoot-loadingBelow", listContent)[0];
        if (!lowerWhiteSpaceElement) {
            lowerWhiteSpaceElement = $("<div class='listRoot-loading listRoot-loadingBelow' style='display: none;'><span>" + $dyn.label('List_LoadingMoreData') + "</span></div>")[0];
            listContent.insertBefore(lowerWhiteSpaceElement, targetElement.nextSibling);
        }

        // Text elements in white space node
        var whiteSpaceTexts = $(listContent).find(".listRoot-loading span");

        var _lastKnownWhiteSpaceWidth;
        function updateWhiteSpaceWidth() {
            var width = listContext.GetScrollRegionWidth();
            if (width !== _lastKnownWhiteSpaceWidth && width !== 0) {
                _lastKnownWhiteSpaceWidth = width;
                whiteSpaceTexts.css("max-width", width);
            }
        }

        // In infinite scrolling mode, scrolling is a complex function of many parameters
        // Let's track those parameters based on scroll events
        var scrollParameters;

        var lastRequestedPageSize;
        var upperWhiteSpaceVisible;
        var lowerWhiteSpaceVisible;
        var upperWhiteSpaceHeight = 0;
        var lowerWhiteSpaceHeight = 0;
        var scrollStateAdjusted;
        // Adjusts scroll triggers and other parameters based on the loaded data
        function adjustScrollParameters(rowHeight, renderedRowCount, data) {
            upperWhiteSpaceHeight = 0;
            lowerWhiteSpaceHeight = 0;

            rowHeight = rowHeight || 0;
            var cacheSize = $dyn.value(data.CacheSize) || 0;
            var fetchedDataStartPosition = $dyn.value(data.FetchedDataStartPosition) || 0;
            var dataAbove = $dyn.value(data.DataAbove);
            var dataBelow = $dyn.value(data.DataBelow);

            var bufferAbove = (listContext.clientCacheStart !== undefined && (dataAbove || listContext.clientCacheStart > 0));
            var bufferBelow = (listContext.clientCacheStart !== undefined && (dataBelow || (listContext.clientCacheStart + renderedRowCount) < cacheSize));

            // If renderer is in the restricted rendering mode, not all rows from collection may be rendered/removed/updated
            // That's why we shouldn't change 'Loading More Data' buffer visibility as it may not represent real Data Source state
            if (listContext._renderer.restrictedRenderingMode()) {
                bufferAbove = upperWhiteSpaceVisible;
                bufferBelow = lowerWhiteSpaceVisible;
            }

            if (upperWhiteSpaceVisible !== bufferAbove) {
                upperWhiteSpaceVisible = bufferAbove;
            }

            if (lowerWhiteSpaceVisible !== bufferBelow) {
                lowerWhiteSpaceVisible = bufferBelow;
            }

            if (!upperWhiteSpaceHeight && bufferAbove) {
                upperWhiteSpaceHeight = $(upperWhiteSpaceElement).outerHeight();
            }

            if (!lowerWhiteSpaceHeight && bufferBelow) {
                lowerWhiteSpaceHeight = $(lowerWhiteSpaceElement).outerHeight();
            }

            // Upper padding
            var bufferAboveHeight = 0;
            var bufferBelowHeight = 0;
            if (bufferAbove) {
                bufferAboveHeight = upperWhiteSpaceHeight;
            }

            if (bufferBelow) {
                bufferBelowHeight = lowerWhiteSpaceHeight;
            }
            var scrollViewportHeight = listContext.GetScrollRegionHeight();
            // Request more data when a half a scrollviewport away from the top or bottom
            var dataTriggerMin = bufferAboveHeight + 0.5*scrollViewportHeight;
            var dataTriggerMax = listContext._renderer.getRenderedRowCount() * listContext._renderer.getRowHeight() + bufferAboveHeight - 1.5*scrollViewportHeight;

            // Apply pending scroll shifts to account for any cache expansion causing the origin of the scroll to change
            applyPendingScrollShifts();

            // Keep the scroll params around until updated
            scrollParameters = listContext._scrollParameters = {
                cacheSize: cacheSize,
                fetchedDataStartPosition: fetchedDataStartPosition,
                dataAbove: dataAbove,
                dataBelow: dataBelow,
                dataTriggerMax: dataTriggerMax,
                dataTriggerMin: dataTriggerMin,
                rowHeight: rowHeight,
                renderedRowCount: renderedRowCount,
                bufferAboveHeight: bufferAboveHeight,
                bufferBelowHeight: bufferBelowHeight
            };

            // In case scroll viewport height was 0, we consider scroll state being not adjusted
            scrollStateAdjusted = (scrollViewportHeight !== 0);
        }

        // Manages pending scroll shifts
        var pendingScrollShifts = 0;
        function applyPendingScrollShifts() {
            if (listContext._renderer.restrictedRenderingMode()) {
                return;
            }
            if (pendingScrollShifts !== 0) {
                if (scrollParameters) {
                    var shiftInPixels = pendingScrollShifts * scrollParameters.rowHeight;
                    listContext.SetScrollRegionTop(listContext.GetScrollRegionTop() + shiftInPixels);
                }
                pendingScrollShifts = 0;
            }
        }

        var viewportFull = true;

        // Ensures that the viewport is filled up with rows if it is loaded at some arbitrary location
        function ensureFullViewPortContent() {
            // Just trigger the fetchData logic with current scroll top
            if (scrollParameters) {
                if (!scrollAdjustmentPending) {
                    if (readyForDataFetch()) {
                        tryFetchDataOnScroll();
                    } else {
                        scrollAdjustmentPending = true;
                    }
                }
            }
        }

        function readjustScrollState() {
            if ($dyn.value(listContext.InfiniteScroll)) {
                var rowHeight = listContext._renderer.getRowHeight(targetElement);
                var renderedRowCount = listContext._renderer.getRenderedRowCount();
                adjustScrollParameters(rowHeight, renderedRowCount, listContext);
            }
        }

        var _reflowHandlerTimout;
        function reflowHandler() {
            // Reflow was most likely caused by the grid render
            if (listContext.IsRendering) {
                return;
            }

            if (_reflowHandlerTimout) {
                clearTimeout(_reflowHandlerTimout);
            }
            _reflowHandlerTimout = listContext.setTimeout(function () {
                $dyn.log.list && $dyn.log.list("List: Reflow Handler invoked");

                if ($dyn.value(listContext._allResourcesLoaded)) {
                    listContext.SyncHorizontalScrollOffset();
                    if ($dyn.value(listContext.InfiniteScroll)) {
                        listContext.UpdateScrollRegionHeight();
                    }

                    if (listContext._renderTriggeredWhileHidden && targetElement.offsetParent !== null) {
                        listContext._renderTriggeredWhileHidden = false;
                        listContext.rerender();
                    } else if ($dyn.value(listContext.InfiniteScroll)) {
                        readjustScrollState();
                        ensureFullViewPortContent();
                    }
                }

                _reflowHandlerTimout = undefined;
                if (listContext._reflowHandlerInvoked) {
                    listContext._reflowHandlerInvoked();
                }
            });
        }

        // When list body is resized, handler changes
        function resizeHandler() {
            updateWhiteSpaceWidth();
        }

        // Determines whether LoadData call is going to return any new data based on provided dataStart and DataSize parameters
        function loadDataNeeded(dataStart, dataSize) {
            if (scrollParameters && listContext.clientCacheStart !== undefined && (!listContext.Root || !listContext.Root.IsCloseRequested || !listContext.Root.IsCloseRequested())) {
                // No need to load data that is already rendered
                if (dataSize <= 0 || (dataStart >= listContext.clientCacheStart && dataSize > 0 && (dataStart + dataSize) <= listContext.clientCacheStart + scrollParameters.renderedRowCount)) {
                    $dyn.log.list && $dyn.log.list("List: Loading Data - not needed. Requested start: %s, requested size: %s", dataStart, dataSize);
                    return false;
                }

                // Make sure we are going to load data within current cache or expand current cache
                if (dataStart + dataSize <= 0 && !scrollParameters.dataAbove) {
                    $dyn.log.list && $dyn.log.list("List: Loading Data - not needed. Requested start: %s, requested size: %s", dataStart, dataSize);
                    return false;
                }

                if (dataStart >= scrollParameters.cacheSize && !scrollParameters.dataBelow) {
                    $dyn.log.list && $dyn.log.list("List: Loading Data - not needed. Requested start: %s, requested size: %s", dataStart, dataSize);
                    return false;
                }

                var allowLoad = false;

                // Allow to load data if there is non loaded data above
                if (dataStart < listContext.clientCacheStart && (scrollParameters.dataAbove || listContext.clientCacheStart > 0)) {
                    allowLoad = true;
                }

                // Allow to load data if there is non loaded data below
                if ((dataStart + dataSize) > scrollParameters.renderedRowCount && (scrollParameters.dataBelow || listContext.clientCacheStart + scrollParameters.renderedRowCount < scrollParameters.cacheSize)) {
                    allowLoad = true;
                }

                if (!allowLoad) {
                    $dyn.log.list && $dyn.log.list("List: Loading Data - not needed. Requested start: %s, requested size: %s", dataStart, dataSize);
                }

                return allowLoad;
            }

            return false;
        }

        var lastLoadDataState = {};
        function loadDataStateChanged() {
            if (!scrollParameters || lastLoadDataState.isDirty !== false) {
                return true;
            }

            var stateChanged = !(scrollParameters.dataAbove === lastLoadDataState.dataAbove && scrollParameters.dataBelow === lastLoadDataState.dataBelow &&
                scrollParameters.cacheSize === lastLoadDataState.cacheSize && scrollParameters.fetchedDataStartPosition === lastLoadDataState.fetchedDataStartPosition &&
                scrollParameters.renderedRowCount === lastLoadDataState.renderedRowCount && listContext.clientCacheStart === lastLoadDataState.clientCacheStart);

            if (!stateChanged) {
                $dyn.log.list && $dyn.log.list("List: Loading Data - state didn't change");
            }

            return stateChanged;
        }
        function populateLastLoadDataState() {
            lastLoadDataState = {
                isDirty: false,
                dataAbove: scrollParameters.dataAbove,
                dataBelow: scrollParameters.dataBelow,
                cacheSize: scrollParameters.cacheSize,
                fetchedDataStartPosition: scrollParameters.fetchedDataStartPosition,
                renderedRowCount: scrollParameters.renderedRowCount,
                clientCacheStart: listContext.clientCacheStart
            };
        }

        // Invokes LoadData function when client is not busy and when it makes sense to invoke it
        function invokeLoadData(dataStart, dataSize, scrollDirection) {
            if (loadDataStateChanged() && loadDataNeeded(dataStart, dataSize) && $dyn.value(listContext.Enabled) && $dyn.value(listContext.Visible)) {
                //This keeps track of the last position where from last load data is invoked. Required for fixing scrolling jump issue.
                listContext.clientLastEndPosition = dataStart;
                listContext.prevScrollDirection = scrollDirection;
                dataLoadPending($dyn.value(dataLoadPending) + 1);
                lastRequestedPageSize = dataSize;
                populateLastLoadDataState();
                
                // set a timer to show the loading message
                var loadTimerId = window.setTimeout(function (){ 
                    if (scrollDirection === $dyn.ui.ScrollDirection.up) {
                        upperWhiteSpaceElement.style.display = "block";
                    } else {
                        lowerWhiteSpaceElement.style.display = "block";
                    }
                 }, 500);

                $dyn.callFunction(listContext.LoadData, listContext, [dataStart, dataSize], function loadDataCallback(shift) {
                    // clear the timer if it didnt fire and hide the message
                    window.clearTimeout(loadTimerId);
                    upperWhiteSpaceElement.style.display = "none";
                    lowerWhiteSpaceElement.style.display = "none";

                    shift = parseInt(shift);
                    if (shift > 0) {
                        listContext.applyServerCacheCoordinateShift(shift);
                    }
                    if (scrollPending) {
                        dataLoadedCompletionPending++;
                    }
                    else {
                        dataLoadedRenderPending(true);
                        dataLoadPending($dyn.value(dataLoadPending) - 1);
                    }
                });
            }
        }

        function getDesiredPageSize(scrollHeight) {
            if (scrollParameters && scrollParameters.rowHeight) {
                return 2 * Math.ceil(scrollHeight / scrollParameters.rowHeight);
            }
        }

        /// <summary>
        /// Gets client cache related index of the top most row in viewport
        /// Row is considered to be visible even if it is partially visible
        /// </summary>
        function getFirstVisibleRow() {
            if (scrollParameters && scrollParameters.rowHeight) {
                var scrollTop = listContext.GetScrollRegionTop();
                var firstVisibleRow = Math.floor((scrollTop - scrollParameters.bufferAboveHeight) / scrollParameters.rowHeight);
                return firstVisibleRow;
            }
        }

        function getVisibleRowCount(scrollHeight) {
            var rowHeight = listContext._renderer.getRowHeight(targetElement);
            if (scrollHeight && rowHeight) {
                return Math.ceil(scrollHeight / rowHeight);
            }
        }

        function getDesiredPageStart(scrollHeight) {
            var desiredPageStart;
            if (scrollParameters && scrollParameters.rowHeight && scrollParameters.cacheSize) {
                var firstVisibleRow = getFirstVisibleRow();
                var visibleRowsCount = getVisibleRowCount(scrollHeight);
                var pageSize = lastRequestedPageSize ? lastRequestedPageSize : getDesiredPageSize(scrollHeight);
                if (pageSize < visibleRowsCount) {
                    desiredPageStart = firstVisibleRow;
                } else {
                    desiredPageStart = firstVisibleRow - Math.round((pageSize - visibleRowsCount) / 2);
                }
                desiredPageStart = listContext.ConvertClientCacheIndexToServerCacheIndex(desiredPageStart);
                if (desiredPageStart + pageSize >= scrollParameters.cacheSize) {
                    desiredPageStart = scrollParameters.cacheSize - pageSize;
                }
                if (desiredPageStart < 0) {
                    desiredPageStart = 0;
                }
            }
            return desiredPageStart;
        }
        function infiniteScrollMode() {
            return listContext._fastEditingAllowed() ? $dyn.util.InfiniteScrollMode.slow : $dyn.util.InfiniteScrollMode.fast;
        }

        // Fetches data from server for this scroll operation - the given scrollTop determines what offset to fetch data from
        var dataLoadPending = $dyn.observable(0);
        var scrollPendingTimeoutId;
        var scrollPending;
        var scrollAdjustmentPending;
        var dataLoadedCompletionPending = 0;
        var dataLoadedRenderPending = listContext._dataLoadedRenderPending = $dyn.observable(false);
        function fetchDataOnScroll(scrollTop) {
            var scrollViewportHeight = listContext.GetScrollRegionHeight();

            if (!scrollParameters || !scrollParameters.rowHeight || !scrollViewportHeight || $dyn.shell.isShellInitializing() || listContext.clientCacheStart === undefined) {
                return false;
            }

            // If fast editing is allowed, grid is going to fetch data that covers viewport,
            // instead of just adding new data above or below existing rows
            var _infiniteScrollMode = infiniteScrollMode();

            var requestPageSize = getDesiredPageSize(scrollViewportHeight);
            if (_infiniteScrollMode === $dyn.util.InfiniteScrollMode.slow) {
                var loadData = false;
                var scrollDirection = $dyn.ui.ScrollDirection.none;
                if (scrollTop > scrollParameters.dataTriggerMax) {
                    loadData = true;
                    listContext._scrollDirection = $dyn.ui.ScrollDirection.down;
                } else if (scrollTop < scrollParameters.dataTriggerMin) {
                    loadData = true;
                    listContext._scrollDirection = $dyn.ui.ScrollDirection.up;
                }

                if (loadData) {
                    // Page Start is requested to be above current viewport
                    var pageStart = getDesiredPageStart(scrollViewportHeight);
                    invokeLoadData(pageStart, requestPageSize, scrollDirection);
                }
            } else {
                var clientStart = listContext.clientCacheStart;
                var dataFetched = false;
                if (scrollTop > scrollParameters.dataTriggerMax) {
                    // The position to load new data in client cache coordinate system.
                    var bottomRowPageIndex = listContext._renderer.getBottomRowPageIndex();
                    if (bottomRowPageIndex !== undefined) {
                        var lastRenderedRow = listContext.ConvertPageIndexToServerCacheIndex(bottomRowPageIndex);
                    }

                    if (scrollParameters.dataBelow || lastRenderedRow + 1 < scrollParameters.cacheSize) {
                        var newFetchPosition = lastRenderedRow;
                        
                        //Handle case where the dataTriggerMax is negative and we are starting at the top of a grid
                        if (scrollTop === 0 && listContext.clientLastStartPosition === undefined) {
                            listContext.doNotScrollIntoView = true;
                        }

                        invokeLoadData(newFetchPosition, requestPageSize, $dyn.ui.ScrollDirection.down);
                        dataFetched = true;
                    }
                }
                if (scrollTop < scrollParameters.dataTriggerMin && !dataFetched) {
                    var newFetchBottomPosition = clientStart;
                    if (scrollParameters.dataAbove || newFetchBottomPosition >= 0) {
                        var newFetchPosition = 0;
                        //To handle case when there is data above but the client cache position is 0.
                        //This can happen if before coming to grid page start position of grid is set, check bug 3780108 for example
                        //listContext.clientLastStartPosition undefined means no scrolling is initiated and yet we have data above
                        if (newFetchBottomPosition == 0 && scrollParameters.dataAbove && listContext.clientLastStartPosition === undefined) {
                            listContext.doNotScrollIntoView = true;
                            requestPageSize = listContext._getVisibleRowCount(scrollViewportHeight);
                        }
                        // Adding 1 to have an intersection with existing rows
                        var newFetchPosition = newFetchBottomPosition - requestPageSize + 1;
                        invokeLoadData(newFetchPosition, requestPageSize, $dyn.ui.ScrollDirection.up);
                    }
                }
            }

            return true;
        }

        function tryFetchDataOnScroll() {
            // Where are we in the scroll offset?
            var scrollTop = listContext.GetScrollRegionTop();
            var adjustmentPerformed = false;

            if (scrollParameters) {
                // Are we outside of our scroll triggers?
                if (scrollTop < scrollParameters.dataTriggerMin || scrollTop > scrollParameters.dataTriggerMax) {
                    // We need to fetch some data from server
                    adjustmentPerformed = fetchDataOnScroll(scrollTop);
                }

                // Let's restore selection focus when selected item enters into visible area (maintained by focus triggers)
                if (scrollTop >= scrollParameters.selectionFocusTriggerMin && scrollTop <= scrollParameters.selectionFocusTriggerMax) {
                    listContext._renderer.focusCellInActiveRow();
                    scrollParameters.selectionFocusTriggerMax = undefined;
                    scrollParameters.selectionFocusTriggerMin = undefined;
                }
            }

            return adjustmentPerformed;
        }

        function tryScrollAdjustment() {
            if (readyForDataFetch() && scrollAdjustmentPending) {
                // Adjust scroll state in case it was not successfully adjusted last time
                if (scrollStateAdjusted === false) {
                    readjustScrollState();
                }
                tryFetchDataOnScroll();
                scrollAdjustmentPending = false;
            }
        }

        function readyForDataFetch() {
            return $dyn.value(dataLoadPending) <= 0 && !$dyn.value(dataLoadedRenderPending) && !$dyn.value($dyn.clientBusy);
        }

        listContext.usingDisposable($dyn.observe(dataLoadPending, function () {
            tryScrollAdjustment();
        }));

        listContext.usingDisposable($dyn.observe(dataLoadedRenderPending, function () {
            tryScrollAdjustment();
        }));

        listContext.usingDisposable($dyn.observe($dyn.clientBusy, function () {
            tryScrollAdjustment();
        }));

        // Scroll events handler to react to user scroll position changing
        var verticalScrollTimeoutId;
        var clientViewStartCommandPending = false;

        function pageCoversViewport() {
            if (scrollParameters && scrollParameters.rowHeight && listContext.DataSource) {
                var firstVisibleRow = getFirstVisibleRow();
                var visibleRowsCount = getVisibleRowCount(listContext.GetScrollRegionHeight());
                var lastVisibleRow = firstVisibleRow + visibleRowsCount - 1;
                firstVisibleRow = listContext.ConvertClientCacheIndexToServerCacheIndex(firstVisibleRow);
                lastVisibleRow = listContext.ConvertClientCacheIndexToServerCacheIndex(lastVisibleRow);
                var pageStart = $dyn.value(listContext.FetchedDataStartPosition);
                var pageSize = $dyn.value(listContext.DataSource).length;
                var pageEnd = pageStart + pageSize - 1;

                return pageStart <= firstVisibleRow && pageEnd >= lastVisibleRow;
            }

            return true;
        }

        function setClientViewStart() {
            if (!clientViewStartCommandPending) {
                if (!pageCoversViewport() && scrollParameters && scrollParameters.rowHeight && scrollParameters.cacheSize) {
                    clientViewStartCommandPending = true;
                    $dyn.callFunction(listContext.SetClientViewStart, listContext, [], undefined, undefined, function (commandInteraction) {
                        // Before execute callback
                        clientViewStartCommandPending = false;
                        var desiredPageStart = getDesiredPageStart(listContext.GetScrollRegionHeight());
                        commandInteraction.PositionalParameters = [desiredPageStart];
                        $dyn.log.list && $dyn.log.list("List: SetClientViewStart command is executing with an argument: %s", desiredPageStart);
                    });
                }
            }
        }

        listContext._loadDataNeeded = loadDataNeeded;
        listContext._readjustScrollState = readjustScrollState;
        listContext._updateWhiteSpaceWidth = updateWhiteSpaceWidth;
        listContext._ensureFullViewPortContent = ensureFullViewPortContent;
        listContext._pageCoversViewport = pageCoversViewport;
        listContext._getFirstVisibleRow = getFirstVisibleRow;
        listContext._getDesiredPageStart = getDesiredPageStart;
        listContext._getDesiredPageSize = getDesiredPageSize;
        listContext._getVisibleRowCount = getVisibleRowCount;
        listContext._dataLoadPending = dataLoadPending;
        listContext._dataLoadedRenderPending = dataLoadedRenderPending;
        listContext._infiniteScrollMode = infiniteScrollMode;

        function syncVerticalScrollOffset() {
            if (scrollParameters) {
                if (listContext.UpdateScrollTop()) {
                    lastLoadDataState.isDirty = true;
                }
                setClientViewStart();

                if (!scrollAdjustmentPending) {
                    if (readyForDataFetch()) {
                        tryFetchDataOnScroll();
                    } else {
                        scrollAdjustmentPending = true;
                    }
                }
            }

            if (scrollPendingTimeoutId) {
                clearTimeout(scrollPendingTimeoutId);
            }
            scrollPending = true;

            scrollPendingTimeoutId = setTimeout(function () {
                scrollPendingTimeoutId = undefined;
                scrollPending = false;
                if (dataLoadedCompletionPending > 0) {
                    dataLoadedRenderPending(true);
                    dataLoadPending($dyn.value(dataLoadPending) - dataLoadedCompletionPending);
                    dataLoadedCompletionPending = 0;
                }
            }, 100);
        }

        // Updates the white space offset w.r.t list box scroll region
        function syncWhiteSpacesScrollOffset() {
            whiteSpaceTexts.css("margin-left", listContext.GetScrollRegion().scrollLeft);
        }

        if ($dyn.value(listContext.InfiniteScroll)) {
            listContext._scrollHandler = function () {
                syncWhiteSpacesScrollOffset();
                syncVerticalScrollOffset();
            };
            listContext._resizeHandler = resizeHandler;
        }

        listContext.usingDisposable($dyn.layout.registerEndReflowHandler(listContext.element, reflowHandler));

        listContext._clientCacheStartUpdated = function (clientCacheStartShift) {
            pendingScrollShifts += clientCacheStartShift;
        };
        listContext.usingDisposable($dyn.observe(listContext.InfiniteScroll, function (value) {
            if (!value) {
                listContext._scrollHandler = null;
                listContext._resizeHandler = null;
                scrollParameters = listContext._scrollParameters = null;
                $(upperWhiteSpaceElement).height(0);
                $(lowerWhiteSpaceElement).height(0);
            }
        }));

        listContext.__restoreSelectionFocus = listContext._renderer.restoreSelectionFocus.bind(listContext._renderer);

        return {
            apply: function applyForeach(data, dataContext) {
                var self = this;

                targetElement.dynVirtualContext = {
                    getContext: function () {
                        return listContext._getVirtualControl.apply(listContext, arguments);
                    },
                };

                var collection = data;
                if (data === null || data === undefined) {
                    return;
                }
                if (!(dataContext instanceof $dyn.DataContext)) {
                    throw new TypeError("The argument 'dataContext' must be a an instance of $dyn.DataContext");
                }

                if (!(data instanceof Array)) {
                    collection = data.collection;
                    listContext._deferred = data.deferred;
                    var preRender = data.preRender;
                    var postRender = data.postRender;

                    var updating = data.updating;

                    var skipRenderDuringStaticLoading = data.skipRenderDuringStaticLoading;
                }

                if (selectedItemElement) {
                    if (listContext.UpdateActiveItemBindings) {
                        listContext._selectedItem = $dyn.observable();
                        self.updateSelectedItem = function() {
                            var items = $dyn.value(collection);
                            if (items) {
                                var collectionIndex = $dyn.value(data.selectedIndex) || 0;
                                if (items[collectionIndex]) {
                                    listContext._selectedItem(items[collectionIndex]);
                                }
                            }
                        };
                        self.updateSelectedItem();
                    } else {
                        listContext._selectedItem = $dyn.computed(function () {
                            var items = $dyn.value(collection);
                            var collectionIndex = $dyn.value(data.selectedIndex) || 0;
                            return items && items[collectionIndex];
                        });
                    }

                    var selectedItemDataContext = new $dyn.DataContext(listContext._selectedItem, dataContext);

                    selectedItemElement.setAttribute("data-dyn-row-active", true);

                    role.dataBindItem(selectedItemElement, selectedItemDataContext);
                }

                listContext._headersDataBound = false;
                $dyn.observe(listContext.ShowHeader, function (value) {
                    listContext._header = $(listContext.element).find('.listHeader')[0];
                    if (value && !listContext._headersDataBound) {
                        $dyn.dataBind(listContext._header, listContext.element.dynContext);
                        listContext._headersDataBound = true;
                    }

                    listContext._header.hidden = !value;
                });

                self.resources = function () {
                    var formElement = listContext.Root && listContext.Root.element;
                    var resources;
                    if (formElement) {
                        resources = $dyn.resources.getResource(formElement);
                    } else {
                        resources = [];
                    }

                    return resources;
                };

                var fullRenderPending;

                self.applyForeach = function () {
                    var options = targetElement.dynForeachOptions;
                    collection = options.collection;

                    if (collection !== null && collection !== undefined) {
                        if (targetElement.dynListRendering) {
                            $dyn.log('list binding already pending');
                        }

                        targetElement.dynListRendering = true;

                        var renderForeach = function () {
                            var fullRender = fullRenderPending;
                            fullRenderPending = false;
                            listContext._renderer.renderBody(targetElement, postRender, fullRender);
                        };

                        $dyn.applyFunction(preRender);

                        if (!skipRenderDuringStaticLoading || !$dyn.shell.isPreProcessing || !($dyn.value(dataLoadPending) > 0)) {
                            if (listContext._deferred) {
                                $dyn.async(renderForeach, undefined, "Grid render foreach");
                            } else {
                                renderForeach();
                            }
                        }

                        appliedBinding = true;
                        targetElement.dynListRendering = false;
                    }
                };

                var isShellInitializing = $dyn.peek($dyn.shell.isShellInitializing) ? $dyn.shell.isShellInitializing : false;

                self.list = {
                    collection: collection,
                    groupBy: data.groupBy,
                    rerenderOptions: listContext._rerenderOptions,
                    dataChanged: listContext._dataChangedVersion,
                    dataLoadPending: dataLoadPending,
                    selectedIndex: data.selectedIndex,
                    markedIndexes: listContext.MarkedIndexes,
                    allResourcesLoaded: listContext._allResourcesLoaded,
                    // A dependency is not needed if the shell is not initializing
                    shellInitializing: isShellInitializing,
                    defaultActionEnabled: listContext.NavigationEnabled,
                    columnHeaderMetadata: listContext.ColumnHeaderMetadata,
                    internalSelectedRowId: listContext._dataProvider.InternalSelectedRowId,
                    allowEdit: listContext.AllowEdit,
                    userNotBusyAndSyncInactive: listContext._renderer.userNotBusyAndSyncInactive,
                    rowSyncState: listContext._syncManager.rowSyncState,
                    dataSelectedRowId: listContext._dataProvider.DataSelectedRowId,
                };

                self.applyChanges = function (changes) {
                    var renderRequired = false;
                    var selectedIndexChanged = false;
                    var markingChanged = false;
                    var renderNeeded = false;
                    var headerRenderNeeded = false;
                    var rowSyncStateChanged = false;

                    if ($dyn.shell.isShellInitializing() || !listContext._allResourcesLoaded()) {
                        return;
                    }

                    $dyn.log.list && $dyn.log.list("List: Apply changes %o", changes);

                    if (changes.collection || changes.dataLoadPending || changes.dataChanged || changes.pendingSelectedRow) {
                        renderNeeded = true;
                    }

                    if (changes.allResourcesLoaded || changes.shellInitializing) {
                        headerRenderNeeded = true;
                        fullRenderPending = true;
                        renderNeeded = true;
                    }

                    if (changes.columnHeaderMetadata) {
                        headerRenderNeeded = true;
                        if (!listContext.isResizing) {
                            fullRenderPending = true;
                            renderNeeded = true;
                        }
                    }

                    if (changes.allowEdit || changes.rerenderOptions || changes.groupBy || changes.defaultActionEnabled) {
                        fullRenderPending = true;
                        renderNeeded = true;
                    }

                    if (changes.selectedIndex) {
                        selectedIndexChanged = true;
                    }
                    if (changes.markedIndexes) {
                        markingChanged = true;
                    }

                    if (changes.rowSyncState) {
                        rowSyncStateChanged = true;
                    }

                    //if header rendering it pending , render it first
                    if (headerRenderNeeded) {
                        listContext._renderHeader();
                    }

                    // We should synchronize internal selected row only before updating selected row as updateSelectedRow has a dependency on internal selected row
                    if (changes.userNotBusyAndSyncInactive || changes.dataSelectedRowId || selectedIndexChanged) {
                        // Synchronize internal selected row id to data selected row id when the fast editing is not allowed and when user finished actively interacting with the grid
                        if ($dyn.peek(listContext._renderer.userNotBusyAndSyncInactive()) && !listContext._fastEditingAllowed()) {
                            listContext._dataProvider.syncInternalSelectedRowToDataSelectedRow();
                        }
                    }

                    if (selectedIndexChanged || changes.userNotBusyAndSyncInactive || changes.internalSelectedRowId) {
                        listContext._renderer._updateSelectedRow();
                    }

                    if (renderNeeded) {
                        if (changes.allowEdit) {
                            listContext._renderer._forceRender = true;
                        }

                        if (listContext.UpdateActiveItemBindings && (fullRenderPending || changes.collection)) {
                            self.updateSelectedItem();
                        }

                        // If render needed - render and exit.
                        // Rendering takes care about marking and selection.
                        if (self.render() !== false) {
                            return;
                        }
                    }

                    if (changes.internalSelectedRowId) {
                        var renderer = listContext._renderer;
                        var lastSelectingRowId = renderer._lastSelectingRowId = renderer._selectingRowId;
                        var selectingRowId = renderer._selectingRowId = $dyn.peek(listContext._dataProvider.InternalSelectedRowId);
                        var rowIdToRenderedRow = renderer._rowIdToRenderedRow;
                        var lastRenderRow = rowIdToRenderedRow[lastSelectingRowId];
                        var selectingRenderRow = rowIdToRenderedRow[selectingRowId];
                        renderer._updateSelectingRow(lastSelectingRowId, lastRenderRow && renderer._getRenderedRowElementByRowData(lastRenderRow));
                        renderer._updateSelectingRow(selectingRowId, selectingRenderRow && renderer._getRenderedRowElementByRowData(selectingRenderRow));
                        if (!$dyn.peek(listContext._dataLoadedRenderPending) && selectingRenderRow && selectingRowId !== lastSelectingRowId) {
                            // Let's restore focus to an active cell within user selected row
                            // and scroll user selected row into view
                            if (!renderer.focusCellInActiveRow()) {
                                renderer.scrollUserSelectedRowIntoView();
                            }
                        }
                    }

                    if (markingChanged) {
                        listContext._renderer._markedIndexesChanged();
                    }

                    if (rowSyncStateChanged) {
                        listContext._renderer._rowSyncStateChanged();
                    }
                };

                listContext.usingDisposable($dyn.bulkObserve(self.list, self.applyChanges, updating, self));

                $dyn.observe(
                    self.resources(),
                    function resourcesChanged(resources) {
                        if (resources && resources.length > 0) {
                            var allResourcesLoaded = true;
                            $.each(resources, function (index, value) {
                                // Should be checking if deferred isResolved
                                if (value !== undefined && !value.loaded) {
                                    allResourcesLoaded = false;
                                }
                            });

                            listContext._allResourcesLoaded(allResourcesLoaded);

                            if (!allResourcesLoaded) {
                                // TODO: Handle deferred rejection (when resource failed to load)
                                $.when.apply($.when, resources.map(function (item) { if (!item.loaded) { return item.promise(); } })).done(function () {
                                    listContext._resetCachedInactiveItemElement();
                                    listContext._allResourcesLoaded(true);
                                });
                            }
                        }
                        else {
                            listContext._allResourcesLoaded(true);
                        }
                    }
                );

                self.render = function () {
                    // If some data loading is still pending, do not render
                    if ($dyn.value(dataLoadPending) > 0) {
                        $dyn.log.list && $dyn.log.list("Render: Not rendering because a data load is pending");
                        return false;
                    }

                    // If grid is not visible, do not render
                    // Unless inside an enhanced preview form
                    if (targetElement.offsetParent === null && !listContext.ForceInvisibleRender && !listContext.IsInsidePreviewForm()) {
                        $dyn.log.list && $dyn.log.list("Render: Not visible");
                        listContext._renderTriggeredWhileHidden = true;
                        return false;
                    }

                    // Convert list options to non-observable options
                    var options = {};
                    for (var option in self.list) {
                        options[option] = $dyn.value(self.list[option]);
                    }

                    targetElement.dynForeachOptions = options;
                    self.applyForeach();
                };
            },
            dispose: function () {
                // unbind children if they had been bound
                if (appliedBinding == true) {
                    target.children().each(function (elementIndex, element) {
                        $dyn.unbind(element);
                    });
                    appliedBinding = false;
                }

                if (listContext._selectedRow && !$.contains(targetElement, listContext._selectedRow)) {
                    $dyn.unbind(listContext._selectedRow);
                    listContext._selectedRow = null;
                }

                if (listContext._headersDataBound) {
                    $dyn.unbind(listContext._header);
                    listContext._headersDataBound = false;
                }

                if (scrollPendingTimeoutId) {
                    clearTimeout(scrollPendingTimeoutId);
                }

                if (targetElement.dynVirtualContext) {
                    targetElement.dynVirtualContext = null;
                }

                if (targetElement.dynForeachOptions) {
                    targetElement.dynForeachOptions = null;
                }
            }
        };
    };


    /**
    * @memberof $dyn.controls.List.RowOperationsManager
    */

    /**
     * @class RowOperationsManager
     * @classdesc The RowOperationsManager component of the List control
     * @summary Responsible for handing various row-related events like key down, row click, marking, navigation etc.
     * @internal
     * @memberof $dyn.controls.List
     * @param {Object} listContext - Reference to the listContext object
     * @param {Object} dataProvider - Reference to the DataProvider object
     * @param {Object} renderer - Reference to the Renderer object
     * @param {Object} syncManager - Reference to the SyncManager object
     */
    $dyn.controls.List.RowOperationsManager = function (listContext, dataProvider, renderer, syncManager) {
        this._listContext = listContext;
        this._dataProvider = dataProvider;
        this._renderer = renderer;
        this._syncManager = syncManager;
        /**
        * @memberof $dyn.controls.List.RowOperationsManager#
        * @member
        * @public
        */
        this._activeCell = $dyn.observable("");

        if (this._listContext.ToggleMark) {
            this.ToggleMark = this._listContext.ToggleMark;
        }
    };

    $dyn.controls.List.RowOperationsManager.prototype = {
        _dataProvider: null,
        _renderer: null,
        _syncManager: null,
        _activeCell: null,        

        /**
        * @summary Handles mouse click event within a row
        * @function
        * @memberof $dyn.controls.List.RowOperationsManager#
        * @public
        * @param {Object} event - Mouse click event
        * @description
        * FLOW FOR SELECTION AND MARKING:
        * 1) User clicks a record
        * 2) Update the selection visualization - New row is seen as selected and old row is seen as unselected.
        * 3) Calculate the markMode. i.e. whether it is a single row selection, range selection, toggling or marked records
        * 4) If there are no modifier keys associated with the command and there is defaultAction/jumpRef action on the control then send the secondary navigation command to the server
        * 5) Call FocusRowAndSelectCell which sends the ChangeSelectedIndex to the server with a callback, via other grid components viz. DataProvider and SyncManager
        * 6ai) If changeRow on the server succeeds then ChangeSelectedIndex command has a return value of 1.
        * 6aii) SelectedIndexSubscription is called as the SelectedIndex is updated by the server. This function switches the current active row to be inactive and makes the row at the new selected index as active.
        * 6aiii) Ensures that the visualization controlled by aria-selected and aria-checked is correct on the active row.
        * 6aiv) MarkedIndexes array that stores which records on the grid are marked is filled only after the server returns.
        * 6bi) If changeRow on the server fails then ChangeSelectedIndex command has a return value of 0.
        * 6bii) Callback associated with the ChangeSelectedIndex is invoked which reverses the aria-checked and aria-selected attributes on the records to ensure that the previous record has focus.
        */
        RowClicked: function (event) {
            $dyn.log.list && $dyn.log.list("List: RowClicked");

            var rowElement = $(event.target).closest('.rowTemplate')[0];
            var targetRowId = this._renderer.getInternalRowIdByElement(rowElement);
            var currentSelectedRowId = $dyn.peek(this._dataProvider.InternalSelectedRowId);
            var targetElement = event.target;
            var clickedOnMarkTarget = targetElement && targetElement.classList.contains("checkMarkTarget") ? true : false;
            if (!rowElement || !$dyn.value(this._listContext.Enabled) || (this._listContext._hasTextSelection(event) && !clickedOnMarkTarget)) {
                return;
            }

            var inputMode = this._listContext.InputMode = $dyn.ui.InputType.mouse;
            var index = this._listContext._renderer.getActivePageRelatedIndex(targetRowId);

            var markMode;

            switch (event.which) {
                case $dyn.ui.MouseButtonCodes.leftButton:
                    // Navigation action should be ignored for multiselect mode
                    var ignoreNavigation = (event.ctrlKey || event.shiftKey);
                    if (ignoreNavigation || !this._listContext._isInvalidMarkTarget(event)) {
                        markMode = this.calculateMarkMode(event);
                        $dyn.log.list && $dyn.log.list("List: Mark Mode is " + markMode);
                        if (markMode === $dyn.controls.List.MarkMode.toggle) {
                            if (targetRowId === currentSelectedRowId) {
                                this.ExecuteRowMarkUpdate();
                            }
                        }
                    }
                    if (markMode === $dyn.controls.List.MarkMode.select) {
                        // When you click on the same rowIndex, mark the record only if it is not marked.
                        if (targetRowId === currentSelectedRowId && !this._listContext.MarkedIndexes()[index] && (
                            !$dyn.peek(this._listContext._fastEdit) || (this._listContext._syncManager.isQueueEmpty() && $dyn.peek(this._listContext._fastEdit)))) {
                            this.ExecuteRowMarkUpdate();
                        }
                    }

                    var totalNumberOfRows = $dyn.length(this._listContext.DataSource);

                    this.FocusRowAndSelectCell(null, inputMode, event);
                    if (!ignoreNavigation) {
                        this.OnMouseSecondaryNavigation(event);
                        if (this._listContext && this._listContext.element) {
                            this.formActiveRowChangedNotify();
                        }
                    }

                    break;

                case $dyn.ui.MouseButtonCodes.rightButton:
                    var rowAlreadySelected = this._listContext.MarkedIndexes()[index];
                    this.calculateMarkMode(event);

                    // If the row is already selected, then the right-click should have no effect, so adjust the mark mode appropriately;
                    // otherwise, if the target row ID is the same as the current select row, mark the row.
                    if (rowAlreadySelected && !this._listContext.IsCardList()) {
                        this._listContext._markingAction = $dyn.controls.List.MarkMode.none;
                    } else if (targetRowId === currentSelectedRowId) {
                        this.ExecuteRowMarkUpdate();
                    }
                    this.FocusRowAndSelectCell(null, inputMode, event);
                    break;
            }
        },

        /**
        * @function
        * @memberof $dyn.controls.List.RowOperationsManager#
        * @public
        * @param {Integer} relativePosition - Integer indicating the amount to move relative to the current position.
        * @param {Integer} markMode - The mark mode (Toggle/Select/RangeSelect)
        * @param {Integer} inputMode - Input Mode that triggered this row change (Keyboard/Mouse)
        * @param {Object} event - The event that triggered this row change.
        * @param {Boolean} restrictToRenderedRows - Flag that indicates if the row change is only restricted to rendered rows
        * @summary Handles row change
        */
        FocusRowRelativeToActiveRow: function (relativePosition, markMode, inputMode, event, restrictToRenderedRows) {
            this.FocusRowAndSelectCell(relativePosition, inputMode, event, restrictToRenderedRows);
            if (event && event.preventDefault) {
                event.preventDefault();
            }
        },

        /**
        * @function
        * @memberof $dyn.controls.List.RowOperationsManager#
        * @private
        * @param {Object} event - Event triggering a navigation action (eg: Pressing Enter)
        * @returns {boolean} Whether or not to stop the propagation on the event
        * @summary Conditionally performs the list's navigation action when it is non-editable.
        */
        PerformNonEditModeNavigationAction: function (event) {
            var retVal = false;
            if (!$dyn.value(this._listContext.IsNoResultsMessageDisplayed)) {
                retVal = true;
                var rowElement = this._renderer.getUserSelectedElement();
                var rowId = this._renderer.getInternalRowIdByElement(rowElement);
                if (!$dyn.value(this._listContext.AllowEdit) && $dyn.value(this._listContext.Enabled) && $dyn.value(this._listContext.NavigationAction)
                    && $dyn.value(this._listContext.ForcePrimaryNavigation)) {
    
                    this._syncManager.queueDefaultAction(rowId);
    
                    if (event && event.preventDefault) {
                        event.preventDefault();
                    }
                }
                else if (this._listContext.IsNavigationAction(event) && $dyn.value(this._listContext.NavigationEnabled)) {
                    if (!rowElement) {
                        return false;
                    }
    
                    if (event && event.preventDefault) {
                        event.preventDefault();
                    }

                    // Update server on last selected control so it can retain focus if current grid is still active after navigation action is executed.
                    if (this._listContext.Root && this._listContext.Root.updateLastClientSelectedControl) {
                        this._listContext.Root.updateLastClientSelectedControl();
                    }
    
                    this._syncManager.queueDefaultAction(rowId);
                }
                if (this._listContext && this._listContext.element) {
                    this.formActiveRowChangedNotify();
                }
            }

            return retVal;
        },

        /**
        * @function
        * @memberof $dyn.controls.List.RowOperationsManager#
        * @private
        * @param {Object} event - Row change event that require recalculation of the mark mode (Key Down/ Mouse click)
        * @returns {Integer} An integer representing the Marked Mode
        * @summary Calculates the appropriate mark mode and rangePivot given a jQuery event.
        */
        calculateMarkMode: function (event) {
            var mode;
            var target = $(event.target);

            // If ShowRowLabels(IsSelectionIndicatorVisible) and MultiSelect (IsMarkingEnabled) are both set to No then disable marking.
            if (!$dyn.value(this._listContext.IsMarkingEnabled) && !$dyn.value(this._listContext.IsSelectionIndicatorVisible)) {
                mode = this._listContext._markingAction = $dyn.controls.List.MarkMode.none;
                return mode;
            }

            // If the user clicked on the checkbox then it is the toggle behavior (IsSelectionIndicatorVisible and IsMarkingEnabled both will be true)
            if (event.type === "click" && (target.hasClass('checkMarkTarget') || target.hasClass('markContainer'))) {
                mode = this._listContext._markingAction = $dyn.controls.List.MarkMode.toggle;
                return mode;
            }

            // If IsMarkingEnabled is set on the grid allow multiple records to be marked
            if ($dyn.value(this._listContext.IsMarkingEnabled)) {
                if (event.ctrlKey && event.shiftKey) {
                    mode = $dyn.controls.List.MarkMode.rangeExpansion;
                } else if (event.ctrlKey) {
                    if (event.which == $dyn.ui.KeyCodes.upArrow || event.which == $dyn.ui.KeyCodes.downArrow)
                        mode = $dyn.controls.List.MarkMode.none;
                    else
                        mode = $dyn.controls.List.MarkMode.toggle;
                    // Clear RangeSelection indicating that the shift key is no longer pressed.
                    this._rangeSelection = false;
                } else if (event.shiftKey) {
                    mode = $dyn.controls.List.MarkMode.rangeSelect;
                } else if ($dyn.value(this._listContext.IsSelectionIndicatorVisible)) {
                    // Only if IsSelectionIndicatorVisible (ShowRowLabels) is set to TRUE do implicit marking, i.e. record is marked when selected.
                    mode = $dyn.controls.List.MarkMode.select;
                    // Clear RangeSelection indicating that the shift key is no longer pressed.
                    this._listContext._rangeSelection = false;
                } else if (!$dyn.value(this._listContext.IsSelectionIndicatorVisible)) {
                    // If IsSelectionIndicatorVisible (ShowRowLabels) is set to FALSE then clear existing marking if it exists
                    // and don't mark the record on selection (i.e. dont implicitly mark the record)
                    mode = $dyn.controls.List.MarkMode.clearMark;
                    // Clear RangeSelection indicating that the shift key is no longer pressed.
                    this._listContext._rangeSelection = false;
                }
            }
            else {
                if ($dyn.value(this._listContext.IsSelectionIndicatorVisible)) {
                    // Only if IsSelectionIndicatorVisible (ShowRowLabels) is set to TRUE do implicit marking, i.e. record is marked when selected.
                    mode = $dyn.controls.List.MarkMode.select;
                }
            }

            // If the rangeSelection flag is not set, but the markMode is rangeSelect, then Set the rangePivot and the rangeSelection to true.
            // RangeSelection flag is set to false if an operation other than rangeSelect is performed which should cause the rangePivot to be reset.
            if (!this._listContext._rangeSelection && mode == $dyn.controls.List.MarkMode.rangeSelect) {
                this._listContext._rangeSelection = true;
                this._listContext._rangePivot = this._listContext.SelectedIndex() + $dyn.value(this._listContext.FetchedDataStartPosition);
            }

            this._listContext._markingAction = mode;
            return this._listContext._markingAction;
        },

        /**
        * @function
        * @memberof $dyn.controls.List.RowOperationsManager#
        * @private
        * @returns {Integer} The number of rows from the active row, that need to be marked
        * @summary Calculates the appropriate mark mode and rangePivot given a jQuery event.
        */
        rangePivotWithinPage: function () {
            return this._listContext._rangePivot - $dyn.value(this._listContext.FetchedDataStartPosition);
        },

        /**
        * @function
        * @memberof $dyn.controls.List.RowOperationsManager#
        * @private
        * @summary Calls the SyncManager to queue up a ToggleMark execute action
        */
        ExecuteRowMarkUpdate: function () {
            /// <summary>
            /// Executes a record mark update command.
            /// </summary>
            if (this._listContext._supportsMarking()) {
                this._syncManager.queueToggleMark($dyn.peek(this._dataProvider.InternalSelectedRowId));
            }
        },

        /**
        * @function
        * @memberof $dyn.controls.List.RowOperationsManager#
        * @private
        * @param {Object} event - Event triggering a keyboard secondary navigation (Clicking on a hyperlink using keyboard)
        * @summary Calls the SyncManager to queue up a DefaultAction execute action
        */
        OnKeyboardSecondaryNavigation: function (event) {
            if (!$dyn.value(this._listContext.Enabled)) {
                event.preventDefault();
            }
            else if (this._listContext.IsNavigationAction(event) && $dyn.value(this._listContext.NavigationEnabled)) {
                var rowElement = $(event.target).closest('.rowTemplate')[0];
                if (!rowElement) {
                    return;
                }

                event.preventDefault();
                //clear marked rows before navigating since navigation is not supported with multiselection
                var rowId = this._renderer.getInternalRowIdByElement(rowElement);
                this._syncManager.queueDefaultAction(rowId);
            }
        },

        /**
        * @function
        * @memberof $dyn.controls.List.RowOperationsManager#
        * @private
        * @param {Object} event - Event triggering a keyboard secondary navigation (Clicking on a hyperlink using mouse)
        * @summary Calls the SyncManager to queue up a DefaultAction execute action
        */
        OnMouseSecondaryNavigation: function (event) {
            function preventDefaultAndStopPropagation() {
                event.preventDefault();
                event.stopImmediatePropagation();
                $dyn.log.stopPropagation && $dyn.log.stopPropagation('Mouse Secondary Navigation (Navigation Action)');
            }

            if (!$dyn.value(this._listContext.Enabled)) {
                preventDefaultAndStopPropagation();
            }
            else {
                var rowElement = $(event.target).closest('.rowTemplate')[0];
                if (!rowElement) {
                    return;
                }
                var rowId = this._renderer.getInternalRowIdByElement(rowElement);

                // ForcePrimaryNavigation = is the grid in a lookup
                if (!$dyn.value(this._listContext.AllowEdit) && $dyn.value(this._listContext.Enabled) && $dyn.value(this._listContext.NavigationAction) && $dyn.value(this._listContext.ForcePrimaryNavigation)) {
                    // If the target control is editable inside the readonly grid
                    // then we should not stop the propagation and the default action
                    var controlContext = $dyn.context(event.target);
                    if (!controlContext || !$dyn.value(controlContext.AllowEdit)) {
                        preventDefaultAndStopPropagation();
                    }

                    this._syncManager.queueDefaultAction(rowId);
                }
                else if (this._listContext.IsNavigationAction(event) && $dyn.value(this._listContext.NavigationEnabled)) {
                    if ($dyn.util.clickedText(event.target) || this._listContext.IsCardList()) {
                        // Only preventDefault and navigate if the text is clicked or its a card list. In other cases (such as if the lookup button is clicked) do not navigate.
                        preventDefaultAndStopPropagation();

                        this._syncManager.queueDefaultAction(rowId);
                    }
                }
                else if (!this._listContext.IsValidSecondaryNavigation(event)) {
                    preventDefaultAndStopPropagation();
                }
            }
        },

        /**
        * @function
        * @memberof $dyn.controls.List.RowOperationsManager#
        * @private
        * @param {Object} event - Row change event that requires recomputing the marked indexes
        * @summary Updates the marked indexes array by setting the active row as marked
        */
        ToggleMark: function (event) {
            var markedIndexes = this._dataProvider.MarkedIndexes();
            var rowIndex = this._listContext.SelectedIndex();
            markedIndexes[rowIndex] = markedIndexes[rowIndex] ? false : true;
        },

        /**
        * @function
        * @memberof $dyn.controls.List.RowOperationsManager#
        * @private
        * @param {Integer} relativePosition - Integer indicating the amount to move relative to the current position.
        * @param {Integer} inputMode - Input Mode that triggered this row change (Keyboard/Mouse)
        * @param {Object} event - The event that triggered this row change.
        * @param {Boolean} restrictToRenderedRows - Flag that indicates if the row change is only restricted to rendered rows
        * @summary Focus the row and select the cell
        * @description Updates the internal row id via the Data Provider, which in turn triggers the SyncManager to process the row change
        *              by sending a new row or a change row interaction to the server
        */
        FocusRowAndSelectCell: function (relativePosition, inputMode, event, restrictToRenderedRows) {
            var targetRowId, currentRowId, targetRowElement, rowElement;
            this._listContext.arrowKeyDownTriggered = false;
            $dyn.log.list && $dyn.log.list.info("List: Entering FocusRowAndSelectCell");
            $dyn.log.list && $dyn.log.list.info("List: Row focus, relative: %s, mouse: %s, event: %o",
                relativePosition, inputMode === $dyn.ui.InputType.mouse, event);
            //Decides if we are currently on active row, then if we try to leave ther row we should clear all the messages. Assumption is messages can only be associated to active row.
            if(this._dataProvider.InternalSelectedRowId() == this._dataProvider.DataSelectedRowId()){
                this._listContext.ClearMessages = true;
            }
            else{
                this._listContext.ClearMessages = false;
            }
            //Find current row element
            if (inputMode === $dyn.ui.InputType.mouse) {
                rowElement = $(event.target).closest('.rowTemplate')[0];
                if (rowElement) {
                    currentRowId = this._renderer.getInternalRowIdByElement(rowElement);
                    if (currentRowId !== this._dataProvider.InternalSelectedRowId()) {
                        this.updateActiveElement(rowElement);
                    }
                    var hasInvalidControls = this._listContext.Root ? this._listContext.Root.HasInvalidControls() : false;
                    if (!hasInvalidControls || $dyn.util.waitForClientBusy() || $dyn.value(this._listContext._fastEdit)) {
                        targetRowId = currentRowId;
                        this._activeCell(this._listContext._getParentControlName(event.target));
                        this._dataProvider.changeRow(targetRowId);
                    } else {
                        // Set focus to the invalid control. And log the boundary error
                        var currentSelectionIsDataSelection = this._dataProvider.InternalSelectedRowId() === this._dataProvider.DataSelectedRowId();
                        var targetSelectionIsNotDataSelection = currentRowId !== this._dataProvider.DataSelectedRowId();
                        if (currentSelectionIsDataSelection && targetSelectionIsNotDataSelection) {
                            if (this._listContext.Root) {
                                this._listContext.Root.SetFocusToInvalidControl();
                            } else {
                                $dyn.errorMessageBox({ Text: $dyn.label('BoundaryError'), Label: 'BoundaryError' });
                            }
                        }
                    }
                }
            }
            else {
                rowElement = this._renderer.getUserSelectedElement();
                this._listContext.arrowKeyDownTriggered = true;
                if (rowElement) {
                    currentRowId = this._renderer.getInternalRowIdByElement(rowElement);
                    targetRowId = this._renderer.getRowIdByOffset(relativePosition);
                    if (targetRowId !== this._dataProvider.InternalSelectedRowId()) {
                        this.updateActiveElement(rowElement);
                    }
                    var hasInvalidControls = this._listContext.Root ? this._listContext.Root.HasInvalidControls() : false;
                    if (!hasInvalidControls || $dyn.util.waitForClientBusy() || $dyn.value(this._listContext._fastEdit)) {
                        if (targetRowId) {
                            this._dataProvider.changeRow(targetRowId);
                        } else {
                            if (restrictToRenderedRows) {
                                // We are trying to navigate to a non-existent row. Is this the last row in the db cache and there are no more rows ?
                                // If so, create a new row if the datasource allowCreate and insertAtEnd flags are true
                                // Note: Currently, we don't have these ds properties coming down from the server
                                if (this._dataProvider.isBackendLastRow(currentRowId)) {
                                    if (this._listContext.CreateNewRecordOnKeyDown) {
                                        if ($dyn.peek(this._listContext._fastEdit)) {
                                            //Create new row ahead of server
                                            if ((this._listContext.downArrowKeyUpTriggered === undefined || this._listContext.downArrowKeyUpTriggered) && $dyn.value(this._listContext.AllowCreateOnKeyDown)) {
                                                var childElement = $dyn.util.findFirstFocusableControl(this._listContext.element, undefined, false/*prioritizeEditable*/);
                                                this._activeCell(this._listContext._getParentControlName(childElement));
                                                this._dataProvider.createRow(currentRowId, true);
                                                this._listContext.downArrowKeyUpTriggered = false;
                                            }
                                        }
                                        else {
                                            //Create new row after server processing
                                            this._syncManager.rowKeyDownNewRowInNonFastEditMode();
                                        }
                                    }
                                } else {
                                    if (!$dyn.peek(this._listContext.InfiniteScroll)) {
                                        this._syncManager.queueChangeRowByDelta(currentRowId, relativePosition, false);
                                    }
                                }
                            } else {
                                this._syncManager.queueChangeRowByDelta(currentRowId, relativePosition, false);
                            }
                        }
                    } else {
                        event && event.stopImmediatePropagation();

                        // Set focus to the invalid control
                        this._listContext.Root.SetFocusToInvalidControl();
                    }
                } else {
                    this._syncManager.queueChangeRowByDelta(undefined, relativePosition, false);
                }
            }

            $dyn.log.list && $dyn.log.list.info("List: Leaving FocusRowAndSelectCell");
        },

        /**
        * @function
        * @memberof $dyn.controls.List.RowOperationsManager#
        * @public
        * @param {Object} activeRow - Row change event that requires recomputing the marked indexes
        * @param {Boolean} onlyCheckValueChanged - A flag that determines whether the cell changes have to be sync'ed to the server or not
        * @summary If the element is on the active row, then the SetValue will be sent to server, else a cell change event is raised which is handled by the EditManager
        * @returns {Boolean} return value of updateValue call on active control
        */
        updateActiveElement: function (activeRow, onlyCheckValueChanged) {
            // Call updateValue from the active control so that any value changes are processed before the row change. Note that
            // if the validation fails we need to prevent the row change from happening.
            var valueChanged = false;
            if (document.activeElement) { 
                var activeControlElement = $(document.activeElement).closest('[data-dyn-controlname]').addBack()[0];
                var activeElement = $(activeRow).find(activeControlElement)[0];

                if (activeElement) {

                    var activeControl = $dyn.context(activeElement);

                    if (!activeControl) {
                        this._listContext._getVirtualControl(activeElement);
                        activeControl = $dyn.context(activeElement);
                    }

                    if (activeControl) {
                        if (activeControl.updateValue) {
                            valueChanged = activeControl.updateValue(true, false, onlyCheckValueChanged);
                        }

                        if (activeControl.focusOutCleanup) {
                            activeControl.focusOutCleanup(true);
                        }
                    }
                }
            }

            return valueChanged;
        },

        /**
         * @memberof $dyn.controls.List.RowOperationsManager#
         * @public
         * @summary Function checks if in zoom mode if we are changing row inside a sidepanel close the side panel if we are for responsive
         */
        formActiveRowChangedNotify: function() {
            if ($dyn.util.isInZoomWidthMode() && this.isEementInSidePanel()) {
                // close sidePanel
                var sidePanel = this._listContext.Root.SidePanel;
                if (sidePanel && sidePanel.SidePanelElement && $dyn.context(sidePanel.SidePanelElement).Visible) {
                    // trigger close
                    sidePanel.toggleVisibility();
                }
            }
        },

        /**
         * @memberof $dyn.controls.List.RowOperationsManager#
         * @public
         * @returns {boolean} true if in the sidePanel
         * @summary Function checks if in element is in the side panel
         */
        isEementInSidePanel: function() {
            if (this.elementInSidePanel === undefined) {
            this.elementInSidePanel = false;
            if (this._listContext.element.parentElement && this._listContext.element.parentElement.parentElement && this._listContext.element.parentElement.parentElement.classList.contains("SidePanel")) {
                this.elementInSidePanel = true;
            }
            }
            return this.elementInSidePanel;
        },

        /**
        * @function
        * @memberof $dyn.controls.List.RowOperationsManager#
        * @public
        * @returns {Object} The active cell in the grid
        * @summary Updates the marked indexes array by setting the active row as marked
        */
        getActiveCell: function () {
            return this._activeCell;
        },

        /**
        * @function
        * @memberof $dyn.controls.List.RowOperationsManager#
        * @public
        * @summary Disposes any event listeners
        */
        dispose: function () { },
    };

})();
